import { FormEvent, KeyboardEvent, ReactNode, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import styled, { css, keyframes } from 'styled-components'
import BrwnieLogo from 'components/Renewal/BrwnieLogo'
import { ASSET, LOGIN_PATH, renewalPath } from 'components/Renewal/paths'
import { INDUSTRIES } from 'components/Renewal/nav'
import { useGetBrwnieBasePriceQuery } from 'hooks/query/admin/useGetBrwnieBasePriceQuery'
import usePostSlackMessageSend from 'hooks/query/slackMessageSend/usePostSlackMessageSend'
import LogManager from 'lib/utils/logger'
import { LOGGER } from 'lib/utils/logger/types'
import newsList from 'data/renewalNewsData.json'
import { phoneHyphen } from 'utils/common/phoneHyphen'

const photo = (name: string) => `${ASSET}/photos/${name}`

function loginHref(content: string) {
  return `${LOGIN_PATH}?utm_source=br_landing&utm_medium=cta&utm_campaign=${encodeURIComponent(
    '브라우니 랜딩 페이지'
  )}&utm_content=${content}&utm_term=${encodeURIComponent('서비스신청')}&utm_id=logout`
}

function Chevron() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 6l6 6-6 6" />
    </svg>
  )
}

function Check({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  )
}

const HL = styled.span`
  background: linear-gradient(transparent 58%, #ffd08a 58%, #ffd08a 92%, transparent 92%);
  color: inherit;
  padding: 0 2px;
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
`

const HeadWrap = styled.div<{ $align: 'left' | 'center' }>`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => (p.$align === 'center' ? 'center' : 'flex-start')};
  text-align: ${(p) => p.$align};
  word-break: keep-all;
`

const Eyebrow = styled.p<{ $color: string }>`
  margin: 0 0 14px;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.6;
  letter-spacing: -0.01em;
  color: ${(p) => p.$color};
`

const Title = styled.h2<{ $size: string; $color: string }>`
  margin: 0;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: ${(p) => p.$size};
  font-weight: 800;
  line-height: 1.28;
  letter-spacing: -0.03em;
  color: ${(p) => p.$color};
`

const TitleLine = styled.span`
  display: block;
  overflow: hidden;
  padding-bottom: 0.1em;
  margin-bottom: -0.1em;

  > span {
    display: inline-block;
  }
`

const Desc = styled.p<{ $color: string }>`
  margin: 18px 0 0;
  max-width: 60ch;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: clamp(17px, 1.35vw, 20px);
  font-weight: 400;
  line-height: 1.7;
  color: ${(p) => p.$color};
  text-wrap: pretty;
`

function Head({
  eyebrow,
  eyebrowColor = '#3D5AFE',
  lines,
  desc,
  align = 'left',
  size = 'md',
  titleColor = '#191F28',
  descColor = '#4E5968',
}: {
  eyebrow: string
  eyebrowColor?: string
  lines: ReactNode[]
  desc?: string
  align?: 'left' | 'center'
  size?: 'md' | 'lg'
  titleColor?: string
  descColor?: string
}) {
  const titleSize = size === 'lg' ? 'clamp(34px, 4.4vw, 60px)' : 'clamp(28px, 3.4vw, 46px)'
  return (
    <HeadWrap $align={align}>
      <Eyebrow data-motion="reveal" $color={eyebrowColor}>{eyebrow}</Eyebrow>
      <Title $size={titleSize} $color={titleColor}>
        {lines.map((line, index) => (
          <TitleLine key={index}>
            <span data-motion="line" data-delay={index * 110}>{line}</span>
          </TitleLine>
        ))}
      </Title>
      {desc ? <Desc data-motion="reveal" data-delay={160} $color={descColor}>{desc}</Desc> : null}
    </HeadWrap>
  )
}

const Section = styled.section<{ $bg: string; $color?: string; $pad?: string }>`
  scroll-margin-top: 124px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color || 'inherit'};
  padding: ${(p) => p.$pad || 'clamp(72px, 8vw, 128px) 0'};
`

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 clamp(20px, 3.4vw, 40px);
  box-sizing: border-box;
`

const WORRIES = [
  {
    quote: '딱 한 시간만, 누가 매장 좀 봐 주면 좋겠어요.',
    title: '정해진 요일에 크루가 대신 가요',
    sub: '사장님은 매장에 가지 않아도 돼요',
  },
  {
    quote: '알바가 일주일 만에 그만뒀어요. 또 새로 구하고 설명하기 너무 힘들어요.',
    title: '사람을 구하고 가르칠 일이 없어요',
    sub: '첫날엔 담당 매니저가 크루와 함께 가요',
  },
  {
    quote: '청소할 사람을 못 구해 직접 나가요.',
    title: '청소·보충·점검을 한 번에 해요',
    sub: '끝나면 관리 완료 사진으로 보고해요',
  },
]

const TASKS = [
  ['P-10-s.webp', '크루가 진열대의 상품을 정리하는 모습', '상품 진열 정리'],
  ['P-11-s.webp', '영수증 프린터 용지를 확인하는 손', '영수증 용지 교체'],
  ['P-12-s.webp', '새 봉투를 씌운 휴지통', '휴지통 비우기·청소'],
  ['P-13-s.webp', '매장 비치용 비닐봉투를 보충하는 손', '봉투 등 소모품 보충'],
  ['P-14-s.webp', '진열대 앞 바닥을 청소하는 모습', '바닥 청소'],
  ['P-15-s.webp', '크루가 매장 출입문을 열어 환기하는 모습', '실내 온도 확인·환기'],
  ['P-16-s.webp', '크루가 매장 유리를 닦는 모습', '유리 닦기'],
  ['P-17-s.webp', '크루가 관리 후 휴대폰으로 인증 사진을 찍는 모습', '관리 완료 사진 전달'],
  ['P-18-s.webp', '크루가 천장 조명을 점검하는 모습', '조명 작동 확인'],
] as const

const INDUSTRY_TABS = [
  { name: '아이스크림 할인점', src: 'IND_223165922172_19.webp', alt: '크루가 아이스크림 냉동고 상품을 정리하는 모습', cap: '아이스크림 할인점', tasks: ['상품 보충·진열', '진열대 청소', '냉동고 유리 닦기·성에 확인'] },
  { name: '셀프빨래방', src: 'IND_223165922172_11.jpg', alt: '크루가 세탁기 문 안쪽 고무 패킹을 닦는 모습', cap: '셀프빨래방', tasks: ['건조기 필터 청소', '세탁기 고무 패킹 물때', '세제 등 소모품 보충'] },
  { name: '스터디카페', src: 'IND_223165922172_15.webp', alt: '크루가 스터디카페 좌석을 정리하는 모습', cap: '스터디카페', tasks: ['좌석 얼룩·지우개 가루 청소', '라운지 부자재·소모품 보충', '대여 용품 정리 정돈'] },
  { name: '무인카페', src: 'IND_223165922172_6.jpg', alt: '크루가 무인카페 머신 위에 컵을 보충하는 모습', cap: '무인카페', tasks: ['원두·파우더·컵 보충', '머신 청소', '셀프바 정리'] },
  { name: '무인편의점', src: 'IND_store_2026.jpg', alt: '크루가 무인편의점 과자 매대를 정리하는 모습', cap: '무인편의점', tasks: ['상품 보충·선입선출', '진열대 청소', '냉장고 온도·유리 관리'] },
  { name: '무인문구점', src: 'IND_stationery_2026.webp', alt: '크루가 무인문구점 걸이 진열대에 상품을 거는 모습', cap: '무인문구점', tasks: ['상품 진열·가격표', '상품 먼지·파손 확인', '키오스크 관리'] },
  { name: '무인사진관', src: 'IND_223165922172_3.jpg', alt: '크루가 사진관 프린터 인화지를 교체하는 모습', cap: '무인사진관', tasks: ['인화지 확인·교체', '부스 안 청소', '소품 정리'] },
  { name: '자판기·밀키트', src: 'CASE_223673285591_5-w.jpg', alt: '크루가 무인냉장고에 음료를 보충하는 모습', cap: '자판기·밀키트', tasks: ['비기 전에 보충', '머신 온도·고장 점검', '소모품 관리'] },
  { name: '무인 프린트', src: 'IND_print_2026.jpg', alt: '크루가 복합기 용지함에 용지를 보충하는 모습', cap: '무인 프린트', tasks: ['용지 보충', 'PC 점검·파일 정리', '복합기 점검'] },
  { name: '무인창고', src: 'IND_storage_2026.jpg', alt: '크루가 창고 옷걸이 봉을 점검하는 모습', cap: '무인창고', tasks: ['퇴실 창고 원상 복구', '소화기·누수 점검', '손잡이·키패드 닦기'] },
  { name: '그 외 업종', src: 'STORY_224389030133_5.jpg', alt: '크루가 로봇카페 머신 내부를 정리하는 모습', cap: '로봇카페', tasks: ['무인탁구장·무인빵집·로봇카페 등', '다른 무인매장도 상담해 드려요'] },
]

const FIELD_ROWS = [
  [
    ['IND_223165922172_19.webp', '크루가 냉동고 상품을 정리하는 모습', '아이스크림 할인점'],
    ['IND_223165922172_11.jpg', '크루가 세탁기 문 안쪽을 닦는 모습', '셀프빨래방'],
    ['IND_223165922172_6.jpg', '크루가 무인카페 머신 위에 컵을 보충하는 모습', '무인카페'],
    ['CASE_223666930176_8.jpg', '크루가 스터디카페 칸막이 먼지를 터는 모습', '스터디카페'],
    ['IND_223165922172_3.jpg', '크루가 사진관 프린터 인화지를 교체하는 모습', '무인사진관'],
    ['STORY_224389030133_5.jpg', '크루가 로봇카페 머신 내부를 정리하는 모습', '로봇카페'],
    ['IND_223165952934_6-w.jpg', '크루가 과자 매대를 채우는 모습', '무인편의점'],
    ['IND_223165922172_9.jpg', '크루가 건조기 필터 박스를 진공 청소하는 모습', '셀프빨래방'],
    ['hero-290.webp', '크루가 자판기에 상품을 채우는 모습', '자판기·밀키트'],
    ['hero-287.jpg', '크루가 사진 인화기 리본을 교체하는 모습', '무인사진관'],
    ['IND_print_2026.jpg', '크루가 복합기 용지함에 용지를 보충하는 모습', '무인 프린트'],
  ],
  [
    ['CASE_223318315448_4-w.jpg', '크루가 문구점 진열대 상품을 앞으로 정리하는 모습', '무인문구점'],
    ['CASE_223662251111_5.jpg', '크루가 퇴실 창고 선반을 원래대로 정리하는 모습', '무인창고'],
    ['CASE_223573101861_11.jpg', '원두 호퍼를 가득 채운 커피머신', '무인카페'],
    ['CASE_224157096916_13.jpg', '장갑 낀 손으로 창고 도어락 키패드를 닦는 모습', '무인창고'],
    ['CASE_223673285591_5-w.jpg', '크루가 무인냉장고에 음료를 보충하는 모습', '자판기·밀키트'],
    ['CASE_223666930176_6.jpg', '크루가 스터디카페 책상을 닦는 모습', '스터디카페'],
    ['IND_223165952934_12.jpg', '크루가 쓰레기봉투를 교체하는 모습', '무인편의점'],
    ['hero-293-w.jpg', '크루가 무인문구점 진열대에 상품을 채우는 모습', '무인문구점'],
    ['hero-288.jpg', '크루가 아이스크림 냉동고를 정리하는 모습', '아이스크림 할인점'],
    ['hero-292.webp', '크루가 스터디카페 의자를 정리하는 모습', '스터디카페'],
    ['hero-285.jpg', '크루가 무인 세탁함 화면을 휴대폰으로 촬영하는 모습', '셀프빨래방'],
  ],
]

const STORIES = [
  {
    src: 'int-dc365-store.jpg',
    alt: '과자와 간식이 가득 진열된 무인편의점 DC365 진열대',
    kind: '무인편의점',
    title: '투잡이라 매일 들르기 힘들던 매장',
    quote: '알바가 잘해도 결국 점주 손이 가야 했어요. 브라우니는 맞춘 방식대로 빠짐없이 관리해 줘요.',
    who: 'DC365 무인편의점 사장님 인터뷰',
    href: 'https://blog.naver.com/higherx/222701789533',
    label: 'DC365 사장님 인터뷰 자세히 보기 (새 창)',
  },
  {
    src: 'int-studycafe-forest.jpg',
    alt: '개포 포레 스터디카페에서 장갑을 낀 브라우니 크루가 대여용 독서대를 정리하는 모습',
    kind: '스터디카페',
    title: '한 시간 안에, 기대 이상으로',
    quote: '자기 매장처럼 완벽할 순 없겠지만, 한 시간 안에 기대 이상으로 관리해 주시는 건 분명해요!',
    who: '개포 포레 스터디카페 사장님 인터뷰',
    href: 'https://blog.naver.com/higherx/223161736842',
    label: '개포 포레 스터디카페 사장님 인터뷰 자세히 보기 (새 창)',
  },
  {
    src: 'int-laundry-filter.jpg',
    alt: '셀프빨래방 건조기 먼지 필터의 관리 전(왼쪽)과 관리 후(오른쪽)',
    kind: '셀프빨래방',
    title: '여러 대의 세탁기·건조기도 꼼꼼하게',
    quote: '대형 기기는 놓치는 곳이 많은데, 매장 매뉴얼대로 관리하고 인증샷을 보내 줘서 믿게 됐어요.',
    who: '워시테리아 대치점 사장님 인터뷰',
    href: 'https://blog.naver.com/higherx/223164767767',
    label: '워시테리아 대치점 사장님 인터뷰 자세히 보기 (새 창)',
    compare: true,
  },
]

const FAQS = [
  {
    q: '서비스 범위와 관리 시간은 어떻게 되나요?',
    a: '한 번 방문하면 평균 60분 동안 관리해요.\n무인매장 유형별 특화 업무 3가지와 표준 업무 9가지를 제공하고, 기본으로 매장 내부 청결 관리와 소모품 보충을 해요.\n업종별 전용 업무는 위쪽 [업종별 관리] 메뉴의 업종별 상세 페이지에서 볼 수 있어요.\n다만 아래 서비스는 하지 않아요.\n1. 바닥 물청소나 외벽 청소처럼 특수청소 전문 업체가 하는 서비스 (바닥 물걸레 관리를 원하시면 일회용 청소포와 밀대를 준비해 주세요.)\n2. 화장실 청소 (미리 상의하면 휴지·소독제 같은 소모품 보충은 할 수 있어요.)\n3. 분리수거는 분류된 휴지통에 한해 도와드려요. (쓰레기통 하나에 모든 품목을 버리는 경우 분리수거가 어려울 수 있어요.)\n4. 손이 닿지 않는 곳은 크루가 떨어질 위험이 있어 관리하기 어려워요. (예: 실내등, 천장형 에어컨, 기타 기물)',
  },
  {
    q: '매장 유형이나 평수와 관계없이 서비스 금액은 같나요?',
    a: '스터디카페를 빼면 같아요. 스터디카페는 1회 90분 전용 요금으로 4주 주 2회 348,000원부터예요(VAT 별도).\n다만 아래 경우에는 추가 요금이 생길 수 있어요.\n1. 실평수 40평 이상 매장\n2. 매장 특성상 표준 관리 시간보다 더 관리가 필요한 경우\n3. 표준 업무 외 요청에 따라 추가 업무가 필요한 경우\n첫 방문 때 크루가 실제로 관리하며 현장을 파악한 뒤, 더해질 수 있는 요금을 매니저가 따로 안내해 드려요.',
  },
  {
    q: '서비스는 4주 단위로만 이용할 수 있나요?',
    a: '정기 관리인 4주 패키지, 장기 관리인 12주·24주 패키지 중에서 고를 수 있어요.\n4주 패키지는 정기결제를 선택할 수 있어요. 정기결제를 이용하시면 마지막 관리일 5일 전에 처음 결제한 수단으로 자동 결제해 드려요.',
  },
  {
    q: '계약 취소와 해지는 어떻게 하나요?',
    a: '결제 후 서비스 시작 전에는 홈페이지 [마이페이지]에서 자유롭게 취소·해지를 신청할 수 있어요.\n서비스 시작 후 해지를 원하시면 [마이페이지]에서 해지를 신청하시거나 카카오톡 상담을 요청해 주세요. 빠르게 처리해 드려요.',
  },
  {
    q: '브라우니 크루는 어떤 분들인가요?',
    a: '무인매장 관리에 필요한 이론·실무 교육을 마치고, 엄격한 채용 절차를 거쳐 신원이 확인된 크루들이에요.',
  },
]

const REGIONS = [
  ['서울', '전 지역'],
  ['경기', '성남·수원·광교·하남·일산·광명·과천·부천 등'],
  ['인천', '부평 등 · 일부 지역 제외'],
  ['부산·김해', '일부 지역 제외'],
]

const FranchiseCard = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px clamp(20px, 3vw, 32px);
  border-radius: 24px;
  background: #ffffff;
  border: 1px solid #e5e8eb;
`

const FranchiseRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`

const FranchiseIcon = styled.span`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: #eef2ff;
  color: #003399;
  display: grid;
  place-items: center;
`

const FranchiseCopy = styled.p`
  margin: 0;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #4e5968;
`

const FranchiseTitle = styled.b`
  display: block;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.5;
  color: #191f28;
`

const FranchiseLink = styled(Link)`
  && {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-height: 44px;
    font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.6;
    color: #003399;
    text-decoration: none;
  }
`

function FranchiseNote() {
  return (
    <Section aria-label="프랜차이즈 본사 안내" $bg="#F2F4F6" $pad="clamp(28px, 3vw, 40px) 0">
      <Inner>
        <FranchiseCard>
          <FranchiseRow>
            <FranchiseIcon>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 21h18M5 21V8l7-4 7 4v13M9 21v-6h6v6" />
              </svg>
            </FranchiseIcon>
            <FranchiseCopy>
              <FranchiseTitle>매장이 여러 곳이세요?</FranchiseTitle>
              프랜차이즈 본사·다점포 관리는 따로 안내해 드려요.
            </FranchiseCopy>
          </FranchiseRow>
          <FranchiseLink to={renewalPath('/business')}>
            자세히 보기
            <Chevron />
          </FranchiseLink>
        </FranchiseCard>
      </Inner>
    </Section>
  )
}

export default function RenewalHomeSections() {
  return (
    <>
      <WorrySection />
      <VideoSection />
      <ScopeSection />
      <ReportSection />
      <FieldSection />
      <StoriesSection />
      <FitSection />
      <PriceSection />
      <StartSection />
      <AfterSection />
      <CrewSection />
      <AreaSection />
      <NewsSection />
      <ApplySection />
      <FranchiseNote />
    </>
  )
}

function WorrySection() {
  return (
    <Section id="worry" aria-label="사장님 마음" $bg="#FFFFFF">
      <Inner>
        <Head
          eyebrow="사장님 마음"
          lines={['무인매장인데,', <>왜 이렇게 <HL>손이 많이 갈까요</HL></>]}
          desc="사장님들께 자주 들은 말이에요."
        />
        <WorryList data-motion="swap" data-gap="900">
          {WORRIES.map((item) => (
            <WorryCard key={item.quote}>
              <WorrySide>
                <Pill $tone="gray">고민</Pill>
                <Quote data-w="">“{item.quote}”</Quote>
              </WorrySide>
              <WorrySide data-s="">
                <Pill $tone="blue">해결</Pill>
                <div>
                  <Solve>{item.title}</Solve>
                  <SolveSub>{item.sub}</SolveSub>
                </div>
              </WorrySide>
            </WorryCard>
          ))}
        </WorryList>
        <WorryBand>
          <WorryBandText>
            이 일, <HL>브라우니 크루가</HL> 대신할게요.
          </WorryBandText>
          <BlueButton to={loginHref('home_worry')}>
            관리 신청하기
            <Arrow />
          </BlueButton>
        </WorryBand>
      </Inner>
    </Section>
  )
}

const VIDEO_ID = 'uxEUy9NIDgo'
const VIDEO_TITLE = '정지현 선수 무인매장 관리 영상'
const VIDEO_SRC = `https://www.youtube.com/embed/${VIDEO_ID}?autoplay=1&rel=0&playsinline=1`

function VideoSection() {
  const [playing, setPlaying] = useState(false)

  return (
    <Section id="video" aria-label="브라우니 영상" $bg="#0C2049" $color="#FFFFFF">
      <Inner>
        <VideoHead>
          <VideoKicker>
            <svg width="34" height="34" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 1.5h4l3.2 7.5h-4z" fill="#3D5AFE" />
              <path d="M14 1.5h4l-3.2 7.5h-4z" fill="#7B90FF" />
              <circle cx="12" cy="15.2" r="7" fill="#FFB649" />
              <circle cx="12" cy="15.2" r="5" fill="none" stroke="#E0A100" strokeWidth="1.1" />
              <path d="M12 11.9l1.03 2.09 2.3.33-1.67 1.63.4 2.29L12 17.16l-2.06 1.08.4-2.29-1.67-1.63 2.3-.33z" fill="#FFFFFF" />
            </svg>
            아테네 올림픽 금메달리스트
          </VideoKicker>
          <Title $size="clamp(28px, 3.4vw, 46px)" $color="#FFFFFF">
            <TitleLine>
              <span>
                <span style={{ color: '#FFB649' }}>‘정지현’</span> 레슬링 선수가
              </span>
            </TitleLine>
            <TitleLine>
              <span>무인매장을 관리한다면?</span>
            </TitleLine>
          </Title>
        </VideoHead>
        <VideoFrame>
          {playing ? (
            <iframe
              src={VIDEO_SRC}
              title={VIDEO_TITLE}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <VideoPlay type="button" aria-label={`${VIDEO_TITLE} 재생`} onClick={() => setPlaying(true)}>
              <img src={`https://i.ytimg.com/vi/${VIDEO_ID}/maxresdefault.jpg`} alt="" />
              <VideoShade />
              <PlayKnob aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.5v13l11-6.5z" /></svg>
              </PlayKnob>
            </VideoPlay>
          )}
        </VideoFrame>
      </Inner>
    </Section>
  )
}

function ScopeSection() {
  const [index, setIndex] = useState(0)
  const onKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const key = event.key
    let next: number | null = null
    if (key === 'ArrowRight' || key === 'ArrowDown') next = (index + 1) % INDUSTRY_TABS.length
    if (key === 'ArrowLeft' || key === 'ArrowUp') next = (index - 1 + INDUSTRY_TABS.length) % INDUSTRY_TABS.length
    if (key === 'Home') next = 0
    if (key === 'End') next = INDUSTRY_TABS.length - 1
    if (next === null) return
    event.preventDefault()
    setIndex(next)
    document.getElementById(`ind-tab-${next}`)?.focus()
  }

  return (
    <Section id="scope" aria-label="관리 범위" $bg="#F2F4F6">
      <Inner>
        <Head
          eyebrow="관리 범위"
          lines={['방문할 때마다', <><HL>9가지는 꼭</HL> 해요</>]}
          desc="업종과 상관없이 모든 매장에서 똑같이 해요. 여기에 업종마다 필요한 관리 3가지를 더해요."
        />
        <TaskGrid data-motion="stagger" data-gap="70">
          {TASKS.map(([file, alt, title], i) => (
            <TaskCard key={title}>
              <TaskPhoto>
                <img src={photo(file)} alt={alt} />
              </TaskPhoto>
              <TaskBody>
                <TaskTitle>
                  <TaskNum>{String(i + 1).padStart(2, '0')}</TaskNum>
                  <h3>{title}</h3>
                </TaskTitle>
              </TaskBody>
            </TaskCard>
          ))}
        </TaskGrid>

        <IndustryBlock id="ind">
          <Head
            eyebrow="업종별 관리"
            lines={['어떤 무인매장이든', <><HL>업종에 맞춰</HL> 관리해요</>]}
            desc="업종을 고르면 그 매장에서 크루가 더 챙기는 일을 보여 드려요."
          />
          <TabList role="tablist" aria-label="업종 고르기" onKeyDown={onKey}>
            {INDUSTRY_TABS.map((item, i) => (
              <Tab key={item.name} type="button" role="tab" id={`ind-tab-${i}`} aria-selected={i === index} tabIndex={i === index ? 0 : -1} $on={i === index} onClick={() => setIndex(i)}>
                {item.name}
              </Tab>
            ))}
          </TabList>
          {INDUSTRY_TABS.map((item, i) => (
            <TabPanel key={item.name} role="tabpanel" aria-labelledby={`ind-tab-${i}`} hidden={i !== index}>
              <TabFigure>
                <img src={photo(item.src)} alt={item.alt} />
                <FigCap>{item.cap}</FigCap>
              </TabFigure>
              <TabCopy>
                <TabKicker>업종 전용 관리</TabKicker>
                <TabName>{item.name}</TabName>
                <TaskOl>
                  {item.tasks.map((task, n) => (
                    <TaskLi key={task}>
                      <NumBadge>{n + 1}</NumBadge>
                      {task}
                    </TaskLi>
                  ))}
                </TaskOl>
                <TabFoot>
                  <Together>
                    <Check size={18} />
                    매번 하는 기본 9가지도 함께 해요
                  </Together>
                  <MoreLink to={INDUSTRIES[i].path}>
                    {item.name} 관리 자세히 보기
                    <Chevron />
                  </MoreLink>
                </TabFoot>
              </TabCopy>
            </TabPanel>
          ))}
        </IndustryBlock>
      </Inner>
    </Section>
  )
}

const REPORT_STEPS = [
  ['오후 2:00', '매장 도착', '체크리스트를 열고 관리를 시작해요.', '크루 앱 관리 체크리스트 · 실제 화면', 4000],
  ['오후 2:45', '관리 사진', '관리한 자리를 사진으로 남겨요.', '크루 앱 업무 상세 · 실제 화면', 3000],
  ['오후 2:55', '특이사항', '매장에서 발견한 일을 적어요.', '크루 앱 특이사항 · 실제 화면', 3000],
  ['오후 3:00', '완료 보고', '사장님 휴대폰으로 푸시알림이 가요.', '완료 알림 · 예시 화면', 3000],
  ['그 뒤', '확인·요청', '보고를 확인하고, 필요한 건 댓글로 남겨요.', '관리 보고 댓글 · 예시 화면', 3000],
] as const

const REPORT_SHOTS = [
  [`${ASSET}/app/crew-checklist.webp`, '크루 앱의 실제 관리 체크리스트 화면. 커피머신 알약 청소, 비품 정리 및 채우기, 서비스바 물기 및 먼지 제거, 정수기·커피머신 외부 얼룩 제거 항목마다 사진 버튼이 있어요.'],
  [`${ASSET}/app/crew-task-detail-m.webp`, '크루 앱의 실제 업무 상세 화면. 서비스바 물기 및 먼지 제거 항목에 크루가 남긴 완료 시각과 인증 사진이 보여요.'],
  [`${ASSET}/app/crew-issue.webp`, '크루 앱의 실제 특이사항 화면. 공용공간 싱크대 물때 제거, 제빙기 오염 제거, 매장 입구 발매트 오염 제거, 공용공간 수세미 교체 필요, 입구 유리문 오염 제거 5가지를 적고 사진을 남겼어요.'],
] as const

function ReportSection() {
  const [step, setStep] = useState(0)
  const [progress, setProgress] = useState(0)
  const stepRef = useRef(0)
  const startedRef = useRef(0)
  const playingRef = useRef(true)
  const fingerRef = useRef<HTMLSpanElement>(null)
  const rippleRef = useRef<HTMLSpanElement>(null)
  const arrivalTick = step === 0 ? Math.min(7, Math.floor(progress * 8)) : -1
  const fingerOn = arrivalTick >= 2 && arrivalTick <= 6
  const [fingerX, fingerY] = arrivalTick <= 1 ? [50, 90] : arrivalTick >= 5 ? [30, 57.34] : [89.78, 57.34]
  const reduceMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const replyOn = step === 4 && (reduceMotion || progress >= 2 / 6)
  const [commentIn, setCommentIn] = useState(false)
  const [replay, setReplay] = useState(0)

  const beginStep = (index: number) => {
    playingRef.current = true
    stepRef.current = index
    startedRef.current = performance.now()
    setStep(index)
    setProgress(0)
    setReplay((value) => value + 1)
  }

  const selectStep = (index: number) => {
    if (index === stepRef.current && playingRef.current) {
      playingRef.current = false
      return
    }
    beginStep(index)
  }

  const leaveStep = (index: number) => {
    if (index !== stepRef.current || playingRef.current) return
    beginStep(index)
  }

  useEffect(() => {
    startedRef.current = performance.now()
    let frame = 0
    const tick = (now: number) => {
      if (!playingRef.current) {
        frame = window.requestAnimationFrame(tick)
        return
      }
      const current = stepRef.current
      const duration = REPORT_STEPS[current][4]
      const ratio = (now - startedRef.current) / duration
      if (ratio >= 1) {
        const next = (current + 1) % REPORT_STEPS.length
        stepRef.current = next
        startedRef.current = now
        setProgress(0)
        setStep(next)
      } else {
        setProgress(ratio)
      }
      frame = window.requestAnimationFrame(tick)
    }
    frame = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(frame)
  }, [])

  useEffect(() => {
    const finger = fingerRef.current
    const ripple = rippleRef.current
    if (!finger || !ripple) return
    if (arrivalTick < 1) {
      finger.getAnimations().forEach((animation) => animation.cancel())
      ripple.getAnimations().forEach((animation) => animation.cancel())
      return
    }
    if (arrivalTick !== 3 && arrivalTick !== 6) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !finger.animate) return
    const delay = arrivalTick === 6 ? 380 : 0
    finger.animate([{ transform: 'scale(1)' }, { transform: 'scale(0.78)' }, { transform: 'scale(1)' }], { duration: 380, delay, easing: 'ease-out' })
    ripple.animate([{ transform: 'scale(0.5)', opacity: 0.8 }, { transform: 'scale(2)', opacity: 0 }], {
      duration: 700,
      delay,
      easing: 'cubic-bezier(.2,.6,.3,1)',
    })
  }, [arrivalTick])

  useEffect(() => {
    if (step !== 4) {
      setCommentIn(false)
      return
    }
    setCommentIn(false)
    const frame = window.requestAnimationFrame(() => setCommentIn(true))
    return () => window.cancelAnimationFrame(frame)
  }, [step, replay])

  const reportLate = step > 2

  return (
    <Section id="report" aria-label="관리 완료 보고" $bg="#0C2049" $color="#FFFFFF">
      <Inner>
        <Head
          align="center"
          eyebrow="관리 완료 보고"
          eyebrowColor="#FFD08A"
          titleColor="#FFFFFF"
          descColor="rgba(255,255,255,.84)"
          lines={['매장에 안 가도', <><span style={{ color: '#FFB649' }}>오늘 관리가</span> 보여요</>]}
          desc="크루가 매장에 도착해서 보고를 마치기까지, 사장님 휴대폰에는 이렇게 와요."
        />
        <ReportLayout>
          <ReportList>
            {REPORT_STEPS.map(([time, title, detail], index) => {
              const on = index === step
              return (
                <li key={title}>
                  <ReportButton
                    type="button"
                    aria-pressed={on}
                    $on={on}
                    onClick={() => selectStep(index)}
                    onMouseLeave={() => leaveStep(index)}
                  >
                    <ReportTime $on={on}>{time}</ReportTime>
                    <span>
                      <b>{title}</b>
                      <em>{detail}</em>
                    </span>
                    <ReportBar>
                      <i style={{ width: on ? `${progress * 100}%` : index < step ? '100%' : '0%' }} />
                    </ReportBar>
                  </ReportButton>
                </li>
              )
            })}
          </ReportList>
          <Phone>
            <PhoneScreen>
              <PhoneGlass $gray={reportLate}>
              <PhoneStatus>
                <span>{['2:00', '2:45', '2:55', '3:00', '3:02'][step]}</span>
                <PhoneNotch />
                <span />
              </PhoneStatus>
              <PhoneBar>
                <BrwnieLogo height={18} title="브라우니" />
                <Example>예시 화면</Example>
              </PhoneBar>
              {REPORT_SHOTS.map(([src, alt], index) => (
                <ShotStage key={src} $on={step === index} aria-hidden={step !== index}>
                  <ShotFrame>
                    <ShotImg src={src} alt={step === index ? alt : ''} decoding="async" />
                    {index === 2 ? <IssueLine aria-hidden="true" /> : null}
                    {index === 0 ? (
                      <>
                        <ShotThumb $on={arrivalTick >= 3} aria-hidden="true">
                          <img src={`${ASSET}/app/thumb-4.webp`} alt="" />
                          <ShotCheck>
                            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" aria-hidden="true">
                              <path d="M5 12.5l4.5 4.5L19 7.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </ShotCheck>
                        </ShotThumb>
                        <PointerRipple ref={rippleRef} $x={fingerX} $y={fingerY} aria-hidden="true" />
                        <Pointer ref={fingerRef} $x={fingerX} $y={fingerY} $on={fingerOn} aria-hidden="true" />
                      </>
                    ) : null}
                  </ShotFrame>
                </ShotStage>
              ))}
              {step === 3 ? (
                <Push>
                  <PushLabel>푸시알림 · 지금</PushLabel>
                  <PushCard>
                    <PushHead>
                      <PushIcon><Check /></PushIcon>
                      <b>브라우니</b>
                      <PushTime>오후 3:00</PushTime>
                    </PushHead>
                    <p>매장 관리가 끝났어요.<br />관리 사진과 특이사항을 확인해 보세요.</p>
                    <PushBtn>관리 보고 보기</PushBtn>
                  </PushCard>
                </Push>
              ) : null}
              {step === 4 ? (
                <Comment>
                  <CommentCard $on={commentIn}>
                    <CommentKicker>관리 보고 · 댓글</CommentKicker>
                    <CommentTitle>요청은 댓글로 남겨요</CommentTitle>
                    <CommentList>
                      <Bubble $mine>
                        <small>사장님</small>
                        <p>새 수세미 구입해 두겠습니다.</p>
                      </Bubble>
                      <Bubble $on={replyOn}>
                        <small>담당 크루</small>
                        <p>새 수세미 확인했습니다. 새 수세미로 진행하겠습니다.</p>
                      </Bubble>
                    </CommentList>
                  </CommentCard>
                </Comment>
              ) : null}
              <HomeIndicator aria-hidden="true" />
              </PhoneGlass>
            </PhoneScreen>
            <PhoneCap>{REPORT_STEPS[step][3]}</PhoneCap>
          </Phone>
        </ReportLayout>
      </Inner>
    </Section>
  )
}

function FieldSection() {
  return (
    <Section id="field" aria-label="관리 현장" $bg="#FFFFFF" $pad="clamp(72px, 8vw, 128px) 0 0">
      <Inner>
        <Head
          align="center"
          eyebrow="관리 현장"
          lines={['말보다', <><HL>현장을</HL> 보여 드릴게요</>]}
          desc="브라우니 크루가 실제로 관리한 매장이에요. 2021년부터 관리 기록을 꾸준히 남겨 왔어요."
        />
      </Inner>
      <MarqueeWrap>
        {FIELD_ROWS.map((row, rowIndex) => (
          <FieldRow key={rowIndex} row={row} />
        ))}
      </MarqueeWrap>
      <Inner>
        <FieldAction>
          <OutlineButton to={renewalPath('/cases')}>
            관리 사례 전체 보기
            <Chevron />
          </OutlineButton>
        </FieldAction>
      </Inner>
      <NumberBand>
        <NumberInner>
          <NumberGrid>
            {[
              ['1', '위', '무인매장 관리'],
              ['500', '+', '고객사'],
              ['89,700', '+', '누적 관리 수'],
              ['96', '%', '서비스 유지율'],
            ].map(([num, unit, label]) => (
              <NumberItem key={label}>
                <NumberValue>
                  <b>{num}</b>
                  <span>{unit}</span>
                </NumberValue>
                <NumberLabel>{label}</NumberLabel>
              </NumberItem>
            ))}
          </NumberGrid>
          <NumberNote>2026년 10월 기준 · 자사 집계</NumberNote>
        </NumberInner>
      </NumberBand>
    </Section>
  )
}

function StoriesSection() {
  return (
    <Section id="stories" aria-label="사장님 이야기" $bg="#EEF2FF">
      <Inner>
        <Head
          eyebrow="사장님 이야기"
          lines={['먼저 맡겨 본', <><HL>사장님들</HL> 이야기</>]}
          desc="무인편의점·스터디카페·셀프빨래방 사장님 인터뷰예요."
        />
        <StoryGrid data-motion="stagger">
          {STORIES.map((item) => (
            <Story key={item.who}>
              <StoryPhoto>
                <img src={photo(item.src)} alt={item.alt} />
                {item.compare ? (
                  <>
                    <BeforeTag>관리 전</BeforeTag>
                    <AfterTag>관리 후</AfterTag>
                  </>
                ) : null}
              </StoryPhoto>
              <StoryBody>
                <StoryKind>{item.kind}</StoryKind>
                <StoryTitle>{item.title}</StoryTitle>
                <blockquote>{item.quote}</blockquote>
                <StoryFoot>
                  <b>{item.who}</b>
                  <StoryLink href={item.href} target="_blank" rel="noopener" aria-label={item.label}>
                    자세히 보기
                    <External />
                  </StoryLink>
                </StoryFoot>
              </StoryBody>
            </Story>
          ))}
        </StoryGrid>
        <CenterRow>
          <PaleButton to={renewalPath('/cases')}>
            업종별 관리 사례 보기
            <Chevron />
          </PaleButton>
        </CenterRow>
      </Inner>
    </Section>
  )
}

function FitSection() {
  const { data: prices = [] } = useGetBrwnieBasePriceQuery()
  const startPrice = prices.find(
    (row) =>
      row.storeTypeCd == null &&
      row.totalWeekCount === 4 &&
      row.serviceTimeMinute === 60 &&
      row.weeklyCount === 2 &&
      row.countWeekend === 0
  )
  const fact = startPrice
    ? `1회 60분 · 4주 8회 ${startPrice.servicePrice.toLocaleString('ko-KR')}원부터 · VAT 별도`
    : '1회 60분 · 4주 8회 · VAT 별도'

  const tracks = [
    {
      pill: '정기 관리',
      title: '주 2~7회 정기 관리',
      desc: '크루가 정해진 요일에 와서 청소·상품 진열·소모품 보충·기기 점검을 하고 사진으로 보고해요.',
      fact,
      items: ['매장에 매일 가기 어려워요', '청소·진열·보충·점검을 한 번에 맡기고 싶어요', '관리가 끝날 때마다 사진으로 확인하고 싶어요', '직원 없이 매장을 운영하고 있어요', '정해진 요일에 주 2회 이상 와 주면 좋겠어요'],
      cta: '우리 매장 요금 보기',
      href: '#price',
      external: false,
      sub: '업종별 관리 보기',
      subHref: renewalPath('/industries'),
      links: [] as string[],
    },
    {
      pill: '추가 관리',
      title: '필요할 때 1회, 또는 연 4회',
      desc: '분해나 전문 기기가 필요한 일은 따로 맡겨요. 사진을 보내면 일정과 비용을 안내해요.',
      fact: '건조기 연 1~2회 권장 · 유리창·간판 1회 또는 연 4회',
      items: ['건조기 먼지망 너머 앞판 안쪽이 걱정돼요', '천장형 에어컨 바람에서 냄새가 나요', '유리창·간판 얼룩이 눈에 띄어요', '정기 관리까지는 아니고, 필요할 때 한 번만 맡기고 싶어요', '분해나 전문 기기가 필요한 일이에요'],
      cta: '사진 보내고 견적 받기',
      href: 'https://brwnie.channel.io',
      external: true,
      sub: '',
      subHref: '',
      links: ['건조기 분해 청소', '에어컨 분해 청소', '유리창 청소', '간판·어닝 청소'],
    },
    {
      pill: '프랜차이즈 본사 · 다점포',
      title: '가맹점을 같은 기준으로',
      desc: '가맹점을 한 기준으로 관리하고, 매장별 관리 완료 사진과 이슈를 본사가 받아요.',
      fact: '라운지엑스 24h · 편안창고 · 전담GATE · 프린트데이24 도입',
      items: ['가맹점·직영점이 여러 곳이에요', '매장마다 관리 수준이 달라서 고민이에요', '매장별 관리 사진과 이슈를 본사가 한 번에 받고 싶어요', '점주 대신 본사가 관리 계약을 맡으려고 해요', '새 매장 오픈 때부터 같은 기준으로 관리하고 싶어요'],
      cta: '본사 도입 상담',
      href: 'https://brwnie.channel.io',
      external: true,
      sub: '',
      subHref: '',
      links: [] as string[],
    },
  ]

  return (
    <Section id="fit" aria-label="우리 매장에 맞는 관리" $bg="#F2F4F6">
      <Inner>
        <Head
          eyebrow="우리 매장에 맞는 관리"
          lines={['어떤 관리가', <><HL>우리 매장</HL>에 맞을까요?</>]}
          desc="고민에 따라 맞는 관리가 달라요. 우리 매장과 가까운 쪽을 확인해 보세요."
        />
        <FitGrid data-motion="stagger">
          {tracks.map((track) => (
            <FitCard key={track.pill} aria-label={track.pill}>
              <FitPill>{track.pill}</FitPill>
              <FitTitle>{track.title}</FitTitle>
              <FitDesc>{track.desc}</FitDesc>
              <FitFor>이런 사장님께</FitFor>
              <FitList>
                {track.items.map((item) => (
                  <li key={item}>
                    <FitCheck><Check /></FitCheck>
                    <span>{item}</span>
                  </li>
                ))}
              </FitList>
              <FitFact>{track.fact}</FitFact>
              {track.links.length ? (
                <ChipRow>
                  {track.links.map((link) => (
                    <Chip key={link}>{link}</Chip>
                  ))}
                </ChipRow>
              ) : null}
              <FitActions>
                {track.external ? (
                  <FitCta href={track.href} target="_blank" rel="noopener">
                    {track.cta}
                    <Chevron />
                  </FitCta>
                ) : (
                  <FitCta href={track.href}>
                    {track.cta}
                    <Chevron />
                  </FitCta>
                )}
                {track.sub ? (
                  <MoreLink to={track.subHref}>
                    {track.sub}
                    <Chevron />
                  </MoreLink>
                ) : null}
              </FitActions>
            </FitCard>
          ))}
        </FitGrid>
      </Inner>
    </Section>
  )
}

function findPrice(list: { storeTypeCd: string | null; totalWeekCount: number; serviceTimeMinute: number; weeklyCount: number; countWeekend: number; countWeekday: number; servicePrice: number }[], visits: number, weekend: number) {
  return list.find(
    (row) =>
      row.storeTypeCd == null &&
      row.totalWeekCount === 4 &&
      row.serviceTimeMinute === 60 &&
      row.weeklyCount === visits &&
      row.countWeekend === weekend &&
      row.countWeekday === visits - weekend
  )
}

const WEEKEND_BY_VISITS: Record<number, number[]> = {
  2: [0],
  3: [0, 1],
  4: [0, 1],
  5: [0, 1],
  6: [1, 2],
  7: [2],
}

function PriceSection() {
  const { data: prices = [], isLoading } = useGetBrwnieBasePriceQuery()
  const [visits, setVisits] = useState(5)
  const [weekend, setWeekend] = useState(0)
  const available = WEEKEND_BY_VISITS[visits]
  const chosenWeekend = available.includes(weekend) ? weekend : available[0]
  const row = findPrice(prices, visits, chosenWeekend)
  const pickVisits = (count: number) => {
    const options = WEEKEND_BY_VISITS[count]
    setVisits(count)
    setWeekend((current) => (options.includes(current) ? current : options[0]))
  }
  const combo = !row
    ? ''
    : row.countWeekend === 0
      ? `평일 ${row.countWeekday}일`
      : `평일 ${row.countWeekday}일 + 주말 ${row.countWeekend}일`

  return (
    <Section id="price" aria-label="요금" $bg="#FFFFFF">
      <Inner>
        <Head eyebrow="요금" lines={['요금은', <><HL>미리 보고</HL> 정하세요</>]} desc="4주 단위 · 1회 60분 · VAT 별도" />
        <Calc>
          <CalcForm>
            <Question id="pc-q1">일주일에 몇 번 방문할까요?</Question>
            <ChoiceRow role="group" aria-labelledby="pc-q1">
              {[2, 3, 4, 5, 6, 7].map((count) => (
                <Choice key={count} type="button" aria-pressed={visits === count} $on={visits === count} onClick={() => pickVisits(count)}>
                  주 {count}회
                </Choice>
              ))}
            </ChoiceRow>
            <Question id="pc-q2" $spaced>주말도 포함할까요?</Question>
            <ChoiceRow role="group" aria-labelledby="pc-q2">
              {[['평일만', 0], ['주말 1일', 1], ['주말 2일', 2]].map(([label, day]) => {
                const ok = available.includes(Number(day))
                const on = chosenWeekend === day
                return (
                  <Choice key={label} type="button" aria-pressed={on && ok} disabled={!ok} $wide $on={on && ok} $off={!ok} onClick={() => ok && setWeekend(Number(day))}>
                    {label}
                  </Choice>
                )
              })}
            </ChoiceRow>
            <CalcNote>주말 방문은 서울·수도권만 가능해요. 주 2회는 평일만, 주 6·7회는 주말이 포함돼요.</CalcNote>
          </CalcForm>
          <CalcResult>
            <ResultMeta>{row ? `${combo} · 4주 ${visits * 4}회` : isLoading ? '요금을 불러오는 중이에요' : '이 조건의 요금을 확인하지 못했어요'}</ResultMeta>
            {row ? (
              <ResultPrice aria-live="polite">
                <span>4주</span>
                <PriceAmount>{row.servicePrice.toLocaleString('ko-KR')}</PriceAmount>
                <em>원</em>
              </ResultPrice>
            ) : null}
            <ResultSub>VAT 별도 · 1회 60분 기준 · 공휴일 제외</ResultSub>
            <ResultSub>스터디카페는 1회 90분 전용 요금 · 4주 주 2회 348,000원부터</ResultSub>
            <BadgeRow>
              <Badge>정기 카드결제</Badge>
              <Badge>12주 결제 5% 할인</Badge>
              <Badge>24주 결제 10% 할인</Badge>
            </BadgeRow>
            <ActionRow>
              <WhiteButton to={loginHref('pricing-calc_top')}>
                관리 신청하기
                <Arrow />
              </WhiteButton>
              <GhostButton to={`${renewalPath('/pricing')}#table`}>전체 요금표 보기</GhostButton>
              <ConsultLink to={`${renewalPath('/apply')}?track=regular&visits=${visits}&weekend=${chosenWeekend}`}>이 조건으로 상담받기</ConsultLink>
            </ActionRow>
          </CalcResult>
        </Calc>
        <TermGrid>
          <Term>
            <h3>추가 요금이 붙는 경우</h3>
            <ul>
              <li>실평수 40평 이상 매장</li>
              <li>60분보다 시간이 더 필요해 관리 시간을 30분·60분 늘릴 때</li>
              <li>기본 관리 외 업무를 더할 때</li>
            </ul>
            <strong>첫 방문 후 담당 매니저가 따로 안내해요.</strong>
          </Term>
          <Term>
            <h3>하지 않는 일</h3>
            <ul>
              <li>바닥 물청소·외벽 청소 같은 특수 청소 (물걸레 관리는 일회용 청소포·밀대가 있으면 해요)</li>
              <li>화장실 청소 (휴지·소독제 같은 소모품 보충은 미리 상의하면 해요)</li>
              <li>손이 닿지 않는 높은 곳 (천장 조명·천장형 에어컨 등 — 크루 안전 때문이에요)</li>
              <li>분류되지 않은 쓰레기의 분리수거</li>
            </ul>
          </Term>
          <Term>
            <h3>바꾸거나 그만둘 때</h3>
            <ul>
              <li>다음 결제일 전에 고객센터나 채팅으로 말씀하시면 횟수·요일을 바꿀 수 있어요.</li>
              <li>해지는 마이페이지나 고객센터로 신청해요.</li>
            </ul>
          </Term>
        </TermGrid>
        <PricePromise>
          <PromiseIcon><Check /></PromiseIcon>
          4주 단위로 시작하고, 다음 결제일 전에 언제든 바꾸거나 그만둘 수 있어요.
        </PricePromise>
      </Inner>
    </Section>
  )
}

const STEPS = [
  { no: '01', title: '상담 신청', desc: '성함과 매장명, 연락처만 남겨 주세요.', chat: true },
  { no: '02', title: '담당 매니저 매장 점검', desc: '담당 매니저가 매장에 가서 상태를 확인해요.', src: 'STORY_223133098118_5.jpg', alt: '크루 두 명이 셀프사진관 거울을 닦는 모습' },
  { no: '03', title: '우리 매장 매뉴얼', desc: '매장에 맞는 체크리스트를 먼저 만들어요.', src: 'step-manual-checklist.jpg', alt: '매장 맞춤 체크리스트 앱 화면: 커피머신 청소, 비품 채우기, 서비스바 물기 제거', pos: 'top' },
  { no: '04', title: '담당 크루 배정', desc: '첫날엔 담당 매니저가 크루와 함께 가서 알려 줘요.', src: 'REC_223257938123_6-w.jpg', alt: '담당 매니저가 신규 크루에게 휴대폰 업무 목록으로 교육하는 모습' },
  { no: '05', title: '정기 관리 시작', desc: '정한 요일마다 방문하고 사진으로 보고해요.', src: 'REC_223257938123_17.jpg', alt: '담당 매니저와 크루가 키오스크 앞에서 함께 확인하는 모습' },
]

function StartSection() {
  const blockRef = useRef<HTMLDivElement>(null)
  const fillRef = useRef<HTMLElement>(null)
  const dotsRef = useRef<Array<HTMLSpanElement | null>>([])

  useEffect(() => {
    const block = blockRef.current
    const fill = fillRef.current
    if (!block || !fill) return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const root = document.querySelector('.renewal-root')
    let frame = 0
    const active = dotsRef.current.map(() => false)
    if (reduce) dotsRef.current.forEach((dot) => { if (dot) dot.style.transition = 'none' })
    const paint = () => {
      frame = 0
      const rect = block.getBoundingClientRect()
      const vh = window.innerHeight
      const ratio = reduce ? 1 : Math.max(0, Math.min(1, (vh * 0.85 - rect.top) / (rect.height + vh * 0.3)))
      fill.style.transform = `scaleX(${ratio})`
      const count = dotsRef.current.length
      dotsRef.current.forEach((dot, index) => {
        if (!dot) return
        const on = ratio > 0.01 && ratio >= (index + 0.15) / count
        if (active[index] === on) return
        active[index] = on
        dot.style.backgroundColor = on ? '#003399' : '#DCE3F0'
        dot.style.color = on ? '#FFFFFF' : '#4E5968'
        dot.style.borderColor = on ? '#003399' : '#DCE3F0'
      })
    }
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(paint)
    }
    paint()
    root?.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    const later = window.setTimeout(paint, 400)
    const laterAgain = window.setTimeout(paint, 1200)
    return () => {
      root?.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      window.cancelAnimationFrame(frame)
      window.clearTimeout(later)
      window.clearTimeout(laterAgain)
    }
  }, [])

  return (
    <Section id="start" aria-label="시작하는 방법" $bg="#FFFFFF">
      <Inner>
        <Head
          align="center"
          eyebrow="시작하는 방법"
          lines={[<>신청부터 <HL>첫 방문까지</HL></>, '이렇게 진행돼요']}
          desc="연락처만 남기세요. 그다음은 담당 매니저가 다 챙겨요."
        />
        <ProgressBlock ref={blockRef}>
          <ProgressTrack aria-hidden="true"><i ref={fillRef} /></ProgressTrack>
          <StepGrid>
            {STEPS.map((step, index) => (
              <StepCard key={step.no}>
                {step.chat ? (
                  <ChatMock aria-label="채팅 상담 예시 화면">
                    <ChatHead><i>브</i><b>브라우니 상담</b></ChatHead>
                    <ChatLeft>안녕하세요! 업종과 위치를 알려 주세요.</ChatLeft>
                    <ChatRight>강서구 셀프빨래방, 주 3회요.</ChatRight>
                    <ChatLeft>담당 매니저가 오늘 연락드릴게요.</ChatLeft>
                  </ChatMock>
                ) : (
                  <StepPhoto>
                    <img src={photo(step.src || '')} alt={step.alt} style={{ objectPosition: step.pos || 'center' }} />
                  </StepPhoto>
                )}
                <StepBody>
                  <StepNo ref={(node) => { dotsRef.current[index] = node }}>{step.no}</StepNo>
                  <StepTitle>{step.title}</StepTitle>
                  <StepDesc>{step.desc}</StepDesc>
                </StepBody>
              </StepCard>
            ))}
          </StepGrid>
        </ProgressBlock>
        <ManagerRow>
          <ManagerNote>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#003399" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3l7 3v6c0 4.4-3 7.7-7 9-4-1.3-7-4.6-7-9V6zM9 12l2 2 4-4" /></svg>
            정기 관리 중에도 담당 매니저가 관리 품질을 계속 확인해요
          </ManagerNote>
        </ManagerRow>
        <Assure>
          <AssureLabel>안심 장치</AssureLabel>
          <AssureList>
            {['신원 확인·교육을 통과한 크루', '매번 관리 완료 사진 보고', '매장 이슈 사항 전달', '담당 매니저의 품질 점검'].map((item) => (
              <span key={item}><Check />{item}</span>
            ))}
          </AssureList>
        </Assure>
      </Inner>
    </Section>
  )
}

function AfterSection() {
  const cards = [
    ['01', '크루가 못 오는 날은요?', '방문 시간이나 요일을 바꿔서 그 주 관리를 최대한 진행해요. 내부 매니저 일정이 맞으면 매니저가 대신 가서 관리해 드려요.'],
    ['02', '매장에 문제가 생기면요?', '크루가 관리하다 발견한 이슈는 관리 완료 사진과 함께 앱으로 사장님께 보고해요. 급한 일이면, 유선 또는 카카오톡으로 바로 전달 드려요.'],
    ['03', '방문 요일이나 횟수를 바꾸고 싶으면요?', '주 2~7회 중에서 고르고, 다음 결제일 전에 언제든 바꿀 수 있어요. 4주 단위로 운영돼요.'],
    ['04', '그만두고 싶으면요?', '서비스 시작 전에는 마이페이지에서 바로 취소·해지할 수 있어요. 시작한 뒤에는 마이페이지나 카카오톡 상담으로 신청하면 빠르게 처리해 드려요.'],
    ['05', '결제는 어떻게 되나요?', '4주·12주·24주 패키지 중에서 골라요. 4주 정기결제는 마지막 관리일 5일 전에 처음 결제한 수단으로 자동 결제돼요. 12주 5%·24주 10% 할인.'],
    ['06', '추가 요금이 생길 수도 있나요?', '실평수 40평 이상이거나 표준 관리 시간보다 더 필요한 매장은 추가 요금이 생길 수 있어요. 첫 방문 때 크루가 현장을 파악한 뒤 매니저가 따로 안내해요.'],
  ]
  return (
    <Section id="after" aria-label="시작한 뒤에도" $bg="#EEF2FF">
      <Inner>
        <SplitHead>
          <Head
            eyebrow="시작한 뒤에도"
            lines={['시작한 뒤에 생기는 일도', <><HL>미리</HL> 정해 두었어요</>]}
            desc="크루가 못 오는 날, 매장에 문제가 생긴 날, 플랜을 바꾸고 싶은 날. 이렇게 처리해요."
          />
          <PaleButton to={renewalPath('/faq')}>
            자주 묻는 질문 전체 보기
            <Chevron />
          </PaleButton>
        </SplitHead>
        <AfterGrid data-motion="stagger">
          {cards.map(([no, title, body]) => (
            <AfterCard key={no}>
              <AfterNo>{no}</AfterNo>
              <AfterTitle>{title}</AfterTitle>
              <AfterBody>{body}</AfterBody>
            </AfterCard>
          ))}
        </AfterGrid>
      </Inner>
    </Section>
  )
}

const CREW_SIDE = [
  {
    src: 'crew-thumb-nj.png',
    alt: 'N잡러 크루가 무인매장에서 태블릿으로 관리 기록을 확인하는 모습',
    quote: '“혼자 일할 때 마음이 편하고 할 일만 집중하면 되는 점이 좋았어요.”',
    note: '오전 시간에 매장을 맡는 N잡러 크루예요.',
    href: 'https://blog.naver.com/higherx/223582543867',
    label: 'N잡러 크루 인터뷰 자세히 보기 (새 창)',
  },
  {
    src: 'crew-thumb-kim.png',
    alt: '김은혜 크루가 무인세탁소 건조기를 휴대폰으로 촬영하는 모습',
    quote: '“짧은 시간 근무긴 하지만, 그만큼 책임감도 따르는 작업이라고 생각해요.”',
    note: '사무실 청소 일과 함께 투잡으로 약 1년 일한 크루예요.',
    href: 'https://blog.naver.com/higherx/222996214960',
    label: '김은혜 크루 인터뷰 자세히 보기 (새 창)',
  },
  {
    src: 'crew-thumb-yoo.png',
    alt: '유지민 크루가 무인매장 진열대를 휴대폰으로 촬영하는 모습',
    quote: '“공강시간이나 등교 전 남는 자투리 시간을 활용할 수 있다는 점이 참 만족스러워요.”',
    note: '주 2회, 등교 전 아침에 일하는 대학생 크루예요.',
    href: 'https://blog.naver.com/higherx/222988039694',
    label: '유지민 크루 인터뷰 자세히 보기 (새 창)',
  },
]

const CREW_WORK = [
  {
    title: '첫 관리일엔 담당 매니저가 함께 가요',
    desc: '담당 매니저가 함께 가서 매장을 알려 줘요.',
    icon: 'people',
  },
  {
    title: '체크리스트를 하나씩 지워요',
    desc: '매장마다 만든 체크리스트를 앱에서 하나씩 지우며 일해요.',
    icon: 'list',
  },
  {
    title: '혼자 일해도 혼자가 아니에요',
    desc: '모르는 건 담당 매니저에게 바로 물어요.',
    icon: 'chat',
  },
] as const

const PHOTO_CHECKS = [
  '지폐교환기 정상작동 확인하기',
  '촬영 소품 정리',
  '매장 내 거울 얼룩, 자국 제거하기',
  '점포 문, 정면 유리 얼룩 제거',
  'INK BLUE 부스 사진기 머신 관리',
  'URBAN BLUE 부스 사진기 머신 관리',
  'GRAY 부스 사진기 머신 관리',
  '전 부스 인화지 잔여량 확인',
  '우산꽂이대 밑 쓰레기 제거 및 더러운 부분 닦기',
  '점포 내 바닥에 방치된 쓰레기 / 먼지제거',
  '점포 입구 정리 정돈',
  '마무리 확인(창고 닫힘/열쇠 비치)',
]

const CREW_KITS = ['다목적 클리너', '살균소독수', '유리세정제', '행주']

function CrewWorkIcon({ name }: { name: 'people' | 'list' | 'chat' }) {
  if (name === 'people') {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M16 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 18.5V20M6.5 8a3.5 3.5 0 1 0 7 0a3.5 3.5 0 1 0 -7 0M20 20v-1.5a3.5 3.5 0 0 0-2.5-3.35M15.5 4.6a3.5 3.5 0 0 1 0 6.8" />
      </svg>
    )
  }
  if (name === 'list') {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 6h11M9 12h11M9 18h11M4 6l1 1 2-2M4 12l1 1 2-2M4 18l1 1 2-2" />
      </svg>
    )
  }
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />
    </svg>
  )
}

function CrewSection() {
  return (
    <Section aria-label="브라우니 크루" $bg="#F2F4F6">
      <Inner>
        <Head
          eyebrow="브라우니 크루"
          lines={['매장을 찾아가는 사람들,', <><HL>브라우니 크루</HL></>]}
          desc="신원 확인과 이론·현장 교육을 통과한 크루가 우리 동네 매장을 맡아요. 대학생부터 50대까지, 정해진 요일에 책임지고 관리해요."
        />
        <CrewGrid>
          <CrewFeature>
            <CrewPhoto>
              <img src={photo('REC_222988884615_8.webp')} alt="정재희 크루가 무인 푸드 자판기를 정리하는 뒷모습" />
            </CrewPhoto>
            <CrewCaption>
              <CrewQuote>“제가 약 1년간 근무해보니 사람 스트레스가 없다는 부분이 가장 만족스러웠었어요.”</CrewQuote>
              <CrewNote>1년 동안 600회 넘게 매장을 관리한 크루예요.</CrewNote>
              <CrewLinkRow>
                <CrewMore href="https://blog.naver.com/higherx/222988884615" target="_blank" rel="noopener" aria-label="정재희 크루 인터뷰 자세히 보기 (새 창)">
                  자세히 보기
                  <External />
                </CrewMore>
              </CrewLinkRow>
            </CrewCaption>
          </CrewFeature>
          <CrewSide>
            {CREW_SIDE.map((item) => (
              <CrewMini key={item.href}>
                <CrewThumb>
                  <img src={photo(item.src)} alt={item.alt} />
                </CrewThumb>
                <CrewMiniBody>
                  <CrewMiniQuote>{item.quote}</CrewMiniQuote>
                  <CrewMiniNote>{item.note}</CrewMiniNote>
                  <CrewLinkRow>
                    <CrewMore href={item.href} target="_blank" rel="noopener" aria-label={item.label}>
                      자세히 보기
                      <External />
                    </CrewMore>
                  </CrewLinkRow>
                </CrewMiniBody>
              </CrewMini>
            ))}
          </CrewSide>
        </CrewGrid>
        <CrewWorkTitle>이렇게 일해요</CrewWorkTitle>
        <CrewWorkGrid data-motion="stagger">
          {CREW_WORK.map((item) => (
            <CrewWorkCard key={item.title}>
              <CrewWorkIconBox><CrewWorkIcon name={item.icon} /></CrewWorkIconBox>
              <div>
                <CrewWorkName>{item.title}</CrewWorkName>
                <CrewWorkDesc>{item.desc}</CrewWorkDesc>
              </div>
            </CrewWorkCard>
          ))}
        </CrewWorkGrid>
        <CrewBand>
          <CrewBandCopy>
            <CrewBandKicker>실제 체크리스트예요 — 무인사진관</CrewBandKicker>
            <CrewBandTitle>매장마다 이런 체크리스트를<br />먼저 만들고 시작해요</CrewBandTitle>
            <CrewCheckBlock>
              <CrewCheckLabel>관리 체크리스트</CrewCheckLabel>
              <CrewCheckList>
                {PHOTO_CHECKS.map((item) => (
                  <li key={item}><CrewCheckBox><Check /></CrewCheckBox>{item}</li>
                ))}
              </CrewCheckList>
            </CrewCheckBlock>
            <CrewKitRow>
              <CrewKitLabel>크루가 챙겨 가는 관리 키트</CrewKitLabel>
              {CREW_KITS.map((kit) => <CrewKit key={kit}>{kit}</CrewKit>)}
            </CrewKitRow>
          </CrewBandCopy>
          <CrewPhone>
            <CrewPhoneFrame>
              <CrewPhoneScreen>
                <img src={photo('APP_photo_checklist.jpg')} alt="무인사진관 관리 업무 체크리스트 앱 화면" />
              </CrewPhoneScreen>
            </CrewPhoneFrame>
            <figcaption>실제 앱 화면</figcaption>
          </CrewPhone>
        </CrewBand>
        <CrewApplyRow>
          <CrewApply href="https://higherx.notion.site/b9ccb239d9784e7692cf596c466ef795?source=copy_link" target="_blank" rel="noopener">
            크루로 일하고 싶다면
            <Chevron />
          </CrewApply>
        </CrewApplyRow>
      </Inner>
    </Section>
  )
}

const RegionMap = styled.div`
  margin-top: 24px;
  width: 100%;
  max-width: 240px;
  svg { width: 100%; height: auto; display: block; }
`

const mapPop = keyframes`
  from { transform: scale(0); opacity: 0; }
  to { transform: none; opacity: 1; }
`

const MapDots = styled.g`
  g {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${mapPop} 360ms cubic-bezier(0.16, 0.84, 0.24, 1) both;
  }
  g:nth-child(2) { animation-delay: 320ms; }
  g:nth-child(n + 3) { animation-delay: 360ms; }
  @media (prefers-reduced-motion: reduce) { g { animation: none; } }
`

const MapLabels = styled.g`
  text {
    transform-box: fill-box;
    transform-origin: center;
    font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
    animation: ${mapPop} 360ms cubic-bezier(0.16, 0.84, 0.24, 1) both;
  }
  text:nth-child(1) { animation-delay: 250ms; }
  text:nth-child(2) { animation-delay: 570ms; }
  text:nth-child(n + 3) { animation-delay: 610ms; }
  @media (prefers-reduced-motion: reduce) { text { animation: none; } }
`

function AreaSection() {
  const [open, setOpen] = useState<boolean[]>(() => FAQS.map((_, index) => index === 0))
  return (
    <Section id="area" aria-label="서비스 지역과 자주 묻는 질문" $bg="#FFFFFF">
      <AreaInner>
        <div>
          <Head eyebrow="서비스 지역" lines={['우리 동네도 되나요?']} />
          <RegionList>
            {REGIONS.map(([name, detail]) => (
              <li key={name}><b>{name}</b><span>{detail}</span></li>
            ))}
          </RegionList>
          <WeekendNote>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#003399" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 7h16v13H4zM4 11h16M8 3v4M16 3v4" /></svg>
            주말 방문은 서울·수도권만 가능해요.
          </WeekendNote>
          <RegionButton to={renewalPath('/apply')}>우리 지역 상담하기</RegionButton>
          <RegionMap>
            <svg viewBox="0 0 330 580" role="img" aria-label="서비스 지역 지도: 서울, 인천, 경기, 부산, 김해">
              <path d="M60.7 102.0 L71.2 85.0 L101.1 70.0 L121.4 50.0 L157.8 48.0 L186.1 48.0 L206.3 18.0 L226.5 60.0 L250.8 102.0 L272.6 135.0 L291.2 175.0 L289.6 220.0 L291.2 270.0 L305.0 275.0 L295.3 310.0 L291.2 335.0 L276.7 360.0 L261.3 375.0 L234.6 380.0 L226.5 405.0 L206.3 395.0 L178.0 390.0 L157.8 408.0 L133.5 418.0 L105.2 425.0 L76.9 450.0 L52.6 450.0 L40.5 420.0 L48.5 395.0 L44.5 365.0 L52.6 335.0 L72.8 305.0 L64.7 280.0 L58.2 245.0 L40.5 220.0 L28.3 195.0 L52.6 180.0 L80.9 185.0 L68.8 155.0 L60.7 135.0 Z" fill="#DCE3F0" />
              <ellipse cx="60.7" cy="542" rx="32" ry="15" fill="#DCE3F0" />
              <MapDots>
                {[[95.5, 123], [67.1, 134], [99.5, 154], [249.2, 357], [264.5, 362]].map(([cx, cy]) => (
                  <g key={`${cx}-${cy}`}>
                    <circle cx={cx} cy={cy} r="15" fill="rgba(0,51,153,.16)" />
                    <circle cx={cx} cy={cy} r="7" fill="#003399" stroke="#FFFFFF" strokeWidth="2.5" />
                  </g>
                ))}
              </MapDots>
              <MapLabels>
                <text x="112" y="120" textAnchor="start" fontSize="16" fontWeight="800" fill="#191F28">서울</text>
                <text x="50" y="139" textAnchor="end" fontSize="16" fontWeight="800" fill="#191F28">인천</text>
                <text x="116" y="168" textAnchor="start" fontSize="16" fontWeight="800" fill="#191F28">경기</text>
                <text x="257" y="398" textAnchor="middle" fontSize="16" fontWeight="800" fill="#191F28">부산·김해</text>
              </MapLabels>
            </svg>
          </RegionMap>
        </div>
        <div>
          <Head eyebrow="자주 묻는 질문" lines={['도입 전에 많이 물어보시는 것들']} />
          <FaqList>
            {FAQS.map((item, index) => {
              const expanded = open[index]
              return (
                <FaqItem key={item.q}>
                  <h3>
                    <FaqButton type="button" id={`faq-b-${index}`} aria-expanded={expanded} aria-controls={`faq-p-${index}`} onClick={() => setOpen((value) => value.map((flag, itemIndex) => (itemIndex === index ? !flag : flag)))}>
                      <span><Q>Q</Q>{item.q}</span>
                      <Caret $open={expanded} viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" /></Caret>
                    </FaqButton>
                  </h3>
                  <FaqAnswer id={`faq-p-${index}`} hidden={!expanded}>
                    {item.a.split('\n').map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                  </FaqAnswer>
                </FaqItem>
              )
            })}
          </FaqList>
          <MoreLink to={renewalPath('/faq')}>
            자주 묻는 질문 더 보기
            <Chevron />
          </MoreLink>
        </div>
      </AreaInner>
    </Section>
  )
}

const NewsFallback = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  padding: 20px;
  background: #0c2049;
  color: #ffffff;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.35;
`

const NewsBadge = styled.span`
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 4px 10px;
  border-radius: 8px;
  background: #0c2049;
  color: #ffffff;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.6;
`

function NewsSection() {
  const items = newsList.slice(0, 6)
  return (
    <Section aria-label="브라우니 소식" $bg="#F2F4F6">
      <Inner>
        <SplitHead>
          <Head eyebrow="사장님을 위한 소식" lines={['브라우니 소식']} />
          <NewsAll to={renewalPath('/press')}>전체 보기</NewsAll>
        </SplitHead>
        <NewsGrid>
          {items.map((item) => {
            const imageSrc = item.imageSrc || undefined
            return (
              <NewsCard key={item.newsUrl} href={item.newsUrl} target="_blank" rel="noopener">
                <NewsPhoto>
                  {imageSrc ? <img src={imageSrc} alt="" /> : <NewsFallback>{item.publishing}</NewsFallback>}
                  {item.tag ? <NewsBadge>{item.tag}</NewsBadge> : null}
                </NewsPhoto>
                <NewsTitle>{item.title}</NewsTitle>
                <NewsMeta>{item.date} · {item.publishing}</NewsMeta>
              </NewsCard>
            )
          })}
        </NewsGrid>
      </Inner>
    </Section>
  )
}

const ApplyMark = styled.span`
  background: transparent;
  color: #ffb649;
  padding: 0 2px;
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
`

const ContactText = styled.span`
  display: flex;
  flex-direction: column;
`

const ContactHint = styled.span`
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
`

const ContactPhone = styled.b`
  font-family: Gabarito, Pretendard, sans-serif;
  font-size: 19px;
  font-weight: 800;
  line-height: 1.6;
`

const ContactChat = styled.b`
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.6;
`

const TIMES = ['오전 (10시~12시)', '오후 (12시~15시)', '오후 (15시~18시)', '언제든 괜찮아요']

function ApplySection() {
  const { mutateAsync, isLoading } = usePostSlackMessageSend()
  const [name, setName] = useState('')
  const [store, setStore] = useState('')
  const [phone, setPhone] = useState('')
  const [time, setTime] = useState('')
  const [agree, setAgree] = useState(false)
  const [tried, setTried] = useState(false)
  const [failed, setFailed] = useState(false)
  const [done, setDone] = useState(false)
  const phoneOk = /^01[016789]\d{7,8}$/.test(phone)
  const errors = { name: !name.trim(), store: !store.trim(), phone: !phoneOk, agree: !agree }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (isLoading) return
    setTried(true)
    setFailed(false)
    if (errors.name || errors.store || errors.phone || errors.agree) return
    try {
      await mutateAsync({
        template: 'EASY_CONSULTING',
        data: { name: name.trim(), storeName: store.trim(), phone: phoneHyphen(phone), hopeTime: time.match(/\((.+)\)/)?.[1] || time || '선택 안함', tariffKind: '-' },
      })
      LogManager.Instance.sendLog('generate_lead', {}, LOGGER.GTM)
      setDone(true)
    } catch {
      setFailed(true)
    }
  }

  return (
    <Section id="apply" aria-label="간편 상담" $bg="#003399" $color="#FFFFFF">
      <ApplyInner>
        <div>
          <Head
            eyebrow="간편 상담"
            eyebrowColor="#FFD08A"
            size="lg"
            titleColor="#FFFFFF"
            descColor="rgba(255,255,255,.88)"
            lines={['고민되시면,', <ApplyMark>먼저 물어보세요</ApplyMark>]}
            desc="성함과 매장명, 연락처만 남겨 주시면 무인매장 전문가가 먼저 연락드려요."
          />
          <ContactGrid>
            <ContactCard href="tel:025080913">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFD08A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" /></svg>
              <ContactText>
                <ContactHint>전화 상담 · 평일 10:00~18:00</ContactHint>
                <ContactPhone>02-508-0913</ContactPhone>
              </ContactText>
            </ContactCard>
            <ContactCard href="https://brwnie.channel.io" target="_blank" rel="noopener">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFD08A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" /></svg>
              <ContactText>
                <ContactHint>궁금한 건 채팅으로</ContactHint>
                <ContactChat>채팅 상담하기</ContactChat>
              </ContactText>
            </ContactCard>
          </ContactGrid>
          <ApplyAside>
            바로 시작하실 건가요? <Link to={loginHref('home_apply')}>관리 신청하기 →</Link>
          </ApplyAside>
        </div>
        <ApplyForm aria-label="간편 상담 신청 양식" noValidate onSubmit={submit}>
          {done ? (
            <Done role="status">
              <DoneIcon><Check size={28} /></DoneIcon>
              <DoneTitle>{name.trim()} 님, 상담 신청이 접수됐어요</DoneTitle>
              <DoneBody>남겨주신 번호({phoneHyphen(phone)}) 로 무인매장 전문가가 연락드려요.</DoneBody>
              <Reset type="button" onClick={() => { setDone(false); setName(''); setStore(''); setPhone(''); setTime(''); setAgree(false); setTried(false); setFailed(false) }}>다른 매장도 상담하기</Reset>
            </Done>
          ) : (
            <>
              <FormLead>매장 사정부터 들어 볼게요.</FormLead>
              <FormTitle>무인매장 전문가가<br />빠르게 상담해 드려요.</FormTitle>
              <Label>성함 <Req>*</Req>
                <Field $bad={tried && errors.name} value={name} autoComplete="name" placeholder="성함을 입력해 주세요." onChange={(event) => setName(event.target.value)} />
              </Label>
              {tried && errors.name ? <Alert>성함을 입력해 주세요.</Alert> : null}
              <Label>매장명 <Req>*</Req>
                <Field $bad={tried && errors.store} value={store} placeholder="매장명을 입력해 주세요." onChange={(event) => setStore(event.target.value)} />
              </Label>
              {tried && errors.store ? <Alert>매장명을 입력해 주세요.</Alert> : null}
              <Label>휴대폰 번호 ('-' 없이 입력) <Req>*</Req>
                <Field $bad={tried && errors.phone} value={phone} inputMode="numeric" autoComplete="tel" placeholder="휴대폰 번호를 입력해 주세요." onChange={(event) => setPhone(event.target.value.replace(/\D/g, '').slice(0, 11))} />
              </Label>
              {tried && errors.phone ? <Alert>휴대폰 번호를 다시 확인해 주세요. (예: 01012345678)</Alert> : null}
              <Label>상담 희망 시간 (선택)
                <Select value={time} $empty={!time} onChange={(event) => setTime(event.target.value)}>
                  <option value="">시간을 선택해 주세요</option>
                  {TIMES.map((item) => <option key={item} value={item}>{item}</option>)}
                </Select>
              </Label>
              <Agree>
                <input type="checkbox" checked={agree} onChange={(event) => setAgree(event.target.checked)} />
                (필수) 개인정보 수집·이용에 동의합니다.
              </Agree>
              <Privacy>
                <p>개인정보 수집·이용에 대한 동의를 거부할 권리가 있습니다. 다만, 동의하지 않을 경우 상담 서비스 제공이 불가능합니다.</p>
                <p>수집 항목: 고객명, 매장명, 휴대폰 번호<br />목적: 서비스 상담 등 거래 관계의 설정<br />이용 및 보관 기간: 수집된 개인정보는 정보 기입 후 6개월간 보관됩니다.</p>
              </Privacy>
              {tried && errors.agree ? <Alert>개인정보 수집·이용에 동의해야 상담을 신청할 수 있어요.</Alert> : null}
              {failed ? <Fail>신청이 전송되지 않았어요. 잠시 후 다시 시도하거나 02-508-0913으로 전화 주세요.</Fail> : null}
              <Submit type="submit" disabled={isLoading}>{isLoading ? '보내는 중이에요' : failed ? '다시 신청하기' : '상담 신청하기'}</Submit>
            </>
          )}
        </ApplyForm>
      </ApplyInner>
    </Section>
  )
}

const VideoHead = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`

const VideoKicker = styled.p`
  margin: 0 0 16px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: clamp(16px, 1.4vw, 19px);
  font-weight: 800;
  letter-spacing: -0.01em;
  color: #ffb649;
`

const VideoFrame = styled.div`
  margin-top: clamp(32px, 4vw, 52px);
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: clamp(16px, 2vw, 28px);
  overflow: hidden;
  background: #081430;
  box-shadow: 0 40px 80px -40px rgba(0, 0, 0, 0.6);
  iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; display: block; }
`

const VideoPlay = styled.button`
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  padding: 0;
  background: #081430;
  cursor: pointer;
  display: block;
  img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block; }
`

const VideoShade = styled.span`
  position: absolute;
  inset: 0;
  background: rgba(8, 20, 48, 0.18);
`

const PlayKnob = styled.span`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: clamp(68px, 7.5vw, 104px);
  height: clamp(68px, 7.5vw, 104px);
  border-radius: 50%;
  background: #ffb649;
  color: #191f28;
  display: grid;
  place-items: center;
  box-shadow: 0 18px 40px -16px rgba(255, 182, 73, 0.7);
  svg { width: 38%; height: 38%; }
`

const WorryList = styled.div`
  margin-top: clamp(32px, 4vw, 52px);
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const WorryCard = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 320px), 1fr));
  align-items: center;
  gap: 14px 28px;
  padding: clamp(18px, 2.2vw, 24px) clamp(18px, 2.6vw, 28px);
  border-radius: 20px;
  background: #ffffff;
  border: 1px solid #e5e8eb;
`

const WorrySide = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
`

const Pill = styled.span<{ $tone: 'gray' | 'blue' }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 52px;
  height: 30px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 800;
  box-sizing: border-box;
  ${(p) =>
    p.$tone === 'blue'
      ? css`background: #003399; color: #ffffff;`
      : css`background: #e5e8eb; color: #4e5968;`}
`

const Quote = styled.p`
  margin: 3px 0 0;
  min-width: 0;
  font-size: clamp(16px, 1.5vw, 19px);
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: -0.02em;
  color: #191f28;
  text-decoration: line-through;
  text-decoration-thickness: 2px;
  text-decoration-color: transparent;
`

const Solve = styled.p`
  margin: 0;
  font-size: clamp(16px, 1.5vw, 19px);
  font-weight: 800;
  line-height: 1.5;
  letter-spacing: -0.02em;
  color: #191f28;
`

const SolveSub = styled.p`
  margin: 2px 0 0;
  font-size: 14px;
  color: #4e5968;
`

const WorryBand = styled.div`
  margin-top: 32px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: clamp(22px, 2.6vw, 32px) clamp(22px, 3vw, 40px);
  border-radius: 24px;
  background: #f2f4f6;
`

const WorryBandText = styled.p`
  margin: 0;
  font-size: clamp(22px, 2.1vw, 30px);
  font-weight: 800;
  letter-spacing: -0.025em;
  color: #191f28;
`

const BlueButton = styled(Link)`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 56px;
    padding: 0 26px;
    border-radius: 14px;
    background: #003399;
    color: #ffffff;
    font-size: 17px;
    font-weight: 700;
    white-space: nowrap;
  }
  &&:hover { background: #002a80; }
`

function Arrow() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  )
}

const TaskGrid = styled.div`
  margin-top: clamp(32px, 4vw, 52px);
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(200px, calc(50% - 8px)), 1fr));
  gap: 16px;
`

const TaskCard = styled.article`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #ffffff;
  border: 1px solid #e5e8eb;
  border-radius: 24px;
  overflow: hidden;
  color: #191f28;
  transition: transform 0.25s, box-shadow 0.25s, border-color 0.25s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 22px 40px -26px rgba(12, 32, 73, 0.4);
    border-color: #d1d6db;
  }
`

const TaskPhoto = styled.div`
  aspect-ratio: 4 / 3;
  background: #f2f4f6;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`

const TaskBody = styled.div`
  padding: 20px 22px 22px;
`

const TaskTitle = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  h3 { margin: 0; font-size: 19px; font-weight: 800; line-height: 1.4; letter-spacing: -0.02em; }
`

const TaskNum = styled.span`
  font-size: 16px;
  font-weight: 800;
  color: #003399;
`

const IndustryBlock = styled.div`
  margin-top: clamp(72px, 8vw, 120px);
  scroll-margin-top: 124px;
`

const TabList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  overflow-x: auto;
  margin-top: clamp(28px, 3.4vw, 44px);
  padding: 2px 2px 6px;
  scrollbar-width: none;
`

const Tab = styled.button<{ $on: boolean }>`
  flex-shrink: 0;
  min-height: 48px;
  padding: 0 18px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.02em;
  white-space: nowrap;
  cursor: pointer;
  background: ${(p) => (p.$on ? '#0C2049' : '#FFFFFF')};
  border: 1.5px solid ${(p) => (p.$on ? '#0C2049' : '#D1D6DB')};
  color: ${(p) => (p.$on ? '#FFFFFF' : '#191F28')};
`

const TabPanel = styled.div`
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: clamp(20px, 3.6vw, 52px);
  padding: clamp(12px, 1.6vw, 20px);
  border-radius: 32px;
  background: #f2f4f6;
  &[hidden] { display: none; }
`

const TabFigure = styled.figure`
  margin: 0;
  flex: 1 1 440px;
  min-width: 0;
  position: relative;
  border-radius: 22px;
  overflow: hidden;
  aspect-ratio: 4 / 3;
  background: #e5e8eb;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`

const FigCap = styled.figcaption`
  position: absolute;
  left: 12px;
  bottom: 12px;
  max-width: calc(100% - 24px);
  padding: 5px 10px;
  border-radius: 8px;
  background: rgba(12, 32, 73, 0.84);
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
`

const TabCopy = styled.div`
  flex: 1 1 360px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: clamp(8px, 1.6vw, 24px) clamp(8px, 1.6vw, 24px) clamp(12px, 1.6vw, 24px) 0;
`

const TabKicker = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #3d5afe;
`

const TabName = styled.h3`
  margin: 8px 0 0;
  font-size: clamp(26px, 2.6vw, 36px);
  font-weight: 800;
  line-height: 1.3;
  letter-spacing: -0.03em;
`

const TaskOl = styled.ol`
  margin: 22px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const TaskLi = styled.li`
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 60px;
  padding: 10px 18px;
  border-radius: 16px;
  background: #ffffff;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.02em;
`

const NumBadge = styled.span`
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #eef2ff;
  color: #003399;
  display: grid;
  place-items: center;
  font-size: 15px;
  font-weight: 800;
`

const TabFoot = styled.div`
  margin-top: 22px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 16px;
`

const Together = styled.p`
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #4e5968;
  svg { stroke: #003399; }
`

const MoreLink = styled(Link)`
  && {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-height: 44px;
    font-size: 16px;
    font-weight: 700;
    color: #003399;
  }
`

const ReportLayout = styled.div`
  margin-top: clamp(32px, 4vw, 52px);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: clamp(32px, 5vw, 72px);
`

const ReportList = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  flex: 1 1 380px;
  min-width: 0;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ReportButton = styled.button<{ $on: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: 84px minmax(0, 1fr);
  gap: 0 14px;
  padding: 16px 18px 14px;
  border: 1px solid ${(p) => (p.$on ? 'rgba(255,255,255,.28)' : 'rgba(255,255,255,.1)')};
  border-radius: 18px;
  background: ${(p) => (p.$on ? 'rgba(255,255,255,.1)' : 'transparent')};
  color: #ffffff;
  text-align: left;
  cursor: pointer;
  b { display: block; font-size: 18px; font-weight: 800; letter-spacing: -0.02em; }
  em { display: block; margin-top: 2px; font-style: normal; font-size: 15px; line-height: 1.6; color: rgba(255,255,255,.8); }
`

const ReportTime = styled.span<{ $on: boolean }>`
  font-size: 16px;
  font-weight: 700;
  line-height: 1.6;
  color: ${(p) => (p.$on ? '#FFB649' : 'rgba(255,255,255,.6)')};
`

const ReportBar = styled.span`
  grid-column: 1 / -1;
  margin-top: 12px;
  height: 3px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.12);
  overflow: hidden;
  i {
    display: block;
    height: 100%;
    width: 0;
    background: #ffb649;
  }
`

const Phone = styled.figure`
  margin: 0;
  flex: 0 0 auto;
  width: 300px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
`

const PhoneScreen = styled.div`
  width: 100%;
  padding: 10px;
  border-radius: 46px;
  background: #191f28;
  box-shadow: 0 50px 90px -40px rgba(0, 0, 0, 0.7), inset 0 0 0 1.5px rgba(255, 255, 255, 0.14);
  box-sizing: border-box;
`

const PhoneGlass = styled.div<{ $gray: boolean }>`
  position: relative;
  height: 600px;
  border-radius: 37px;
  overflow: hidden;
  background: ${(p) => (p.$gray ? '#f2f4f6' : '#ffffff')};
  color: #191f28;
  transition: background-color 0.4s ease;
`

const PhoneStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 44px;
  padding: 0 24px;
  font-size: 14px;
  font-weight: 700;
  color: #191f28;
`

const PhoneNotch = styled.span`
  width: 80px;
  height: 22px;
  border-radius: 11px;
  background: #191f28;
`

const PhoneBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 18px 14px;
  svg { height: 18px; width: auto; display: block; }
`

const Example = styled.span`
  padding: 3px 10px;
  border-radius: 999px;
  background: #e5e8eb;
  font-size: 12px;
  font-weight: 700;
  color: #4e5968;
`

const ShotStage = styled.div<{ $on: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  top: 44px;
  bottom: 0;
  z-index: 2;
  overflow: hidden;
  background: #ffffff;
  opacity: ${(p) => (p.$on ? 1 : 0)};
  visibility: ${(p) => (p.$on ? 'visible' : 'hidden')};
`

const ShotFrame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1179 / 2556;
  margin-top: -11.874%;
`

const IssueLine = styled.span`
  position: absolute;
  left: 3.562%;
  top: 24.765%;
  width: 58.015%;
  height: 1.174%;
  background: #ffe08a;
  mix-blend-mode: multiply;
  border-radius: 3px;
  pointer-events: none;
`

const ShotImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
`

const ShotThumb = styled.span<{ $on: boolean }>`
  position: absolute;
  left: 84.224%;
  top: 54.773%;
  width: 11.196%;
  height: 5.164%;
  opacity: ${(p) => (p.$on ? 1 : 0)};
  transform: scale(${(p) => (p.$on ? 1 : 0.5)});
  transition: ${(p) => (p.$on ? 'opacity .2s ease, transform .45s cubic-bezier(.3,1.5,.5,1)' : 'none')};
  pointer-events: none;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    border-radius: 8px;
    box-shadow: 0 0 0 2px #ffffff, 0 4px 10px rgba(0, 0, 0, 0.22);
  }
`

const ShotCheck = styled.span`
  position: absolute;
  right: -5px;
  bottom: -5px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #003399;
  color: #ffffff;
  box-shadow: 0 0 0 1.5px #ffffff;
  display: grid;
  place-items: center;
`

const PointerRipple = styled.span<{ $x: number; $y: number }>`
  position: absolute;
  left: ${(p) => `calc(${p.$x}% - 22px)`};
  top: ${(p) => `calc(${p.$y}% - 22px)`};
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 2px solid #3d5afe;
  box-sizing: border-box;
  opacity: 0;
  pointer-events: none;
`

const Pointer = styled.span<{ $x: number; $y: number; $on: boolean }>`
  position: absolute;
  left: ${(p) => `calc(${p.$x}% - 16px)`};
  top: ${(p) => `calc(${p.$y}% - 16px)`};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(25, 31, 40, 0.3);
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.95), 0 6px 16px rgba(0, 0, 0, 0.28);
  opacity: ${(p) => (p.$on ? 1 : 0)};
  pointer-events: none;
  transition: left 0.45s cubic-bezier(0.4, 0, 0.2, 1), top 0.45s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
`

const HomeIndicator = styled.span`
  position: absolute;
  left: 50%;
  bottom: 8px;
  z-index: 3;
  width: 110px;
  height: 4px;
  margin-left: -55px;
  border-radius: 2px;
  background: rgba(25, 31, 40, 0.35);
`

const Push = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 96px;
  bottom: 0;
  z-index: 1;
  padding: 8px 14px 0;
`

const PushLabel = styled.p`
  margin: 0 0 10px;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  color: #4e5968;
`

const PushCard = styled.div`
  padding: 14px 16px;
  border-radius: 20px;
  background: #ffffff;
  color: #191f28;
  p { margin: 10px 0 0; font-size: 15px; font-weight: 600; line-height: 1.6; }
`

const PushHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  b { font-size: 14px; font-weight: 800; }
`

const PushTime = styled.span`
  margin-left: auto;
  font-size: 12px;
  color: #6b7684;
`

const PushIcon = styled.span`
  flex: none;
  width: 28px;
  height: 28px;
  border-radius: 9px;
  background: #003399;
  color: #ffffff;
  display: grid;
  place-items: center;

  svg { display: block; }
`

const PushBtn = styled.span`
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  border-radius: 10px;
  background: #f2f4f6;
  font-size: 14px;
  font-weight: 800;
  color: #003399;
`

const Comment = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 96px;
  bottom: 0;
  z-index: 1;
  padding: 0 14px;
`

const CommentCard = styled.div<{ $on: boolean }>`
  padding: 16px;
  border-radius: 20px;
  background: #ffffff;
  opacity: ${(p) => (p.$on ? 1 : 0)};
  transform: translateY(${(p) => (p.$on ? '0' : '12px')});
  transition: opacity 0.4s ease, transform 0.4s ease;
`

const CommentList = styled.div`
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const CommentKicker = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #6b7684;
`

const CommentTitle = styled.p`
  margin: 2px 0 0;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #191f28;
`

const Bubble = styled.div<{ $mine?: boolean; $on?: boolean }>`
  align-self: ${(p) => (p.$mine ? 'flex-end' : 'flex-start')};
  max-width: 88%;
  opacity: ${(p) => (p.$on === false ? 0 : 1)};
  transform: translateY(${(p) => (p.$on === false ? '8px' : '0')});
  transition: opacity 0.4s ease, transform 0.4s ease;
  small { display: block; margin: 0 0 4px; font-size: 12px; font-weight: 700; color: #6b7684; text-align: ${(p) => (p.$mine ? 'right' : 'left')}; }
  p {
    margin: 0;
    padding: 10px 14px;
    border-radius: ${(p) => (p.$mine ? '16px 16px 4px 16px' : '16px 16px 16px 4px')};
    background: ${(p) => (p.$mine ? '#003399' : '#F2F4F6')};
    color: ${(p) => (p.$mine ? '#FFFFFF' : '#191F28')};
    font-size: 15px;
    line-height: 1.55;
  }
`

const PhoneCap = styled.figcaption`
  min-height: 20px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.82);
  text-align: center;
`

function FieldRow({ row }: { row: typeof FIELD_ROWS[number] }) {
  const scroller = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState({ left: false, right: false })

  useEffect(() => {
    const el = scroller.current
    if (!el) return
    const update = () => {
      const max = el.scrollWidth - el.clientWidth
      const left = el.scrollLeft > 1
      const right = max - el.scrollLeft > 1
      setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }))
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => {
      el.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [row])

  return (
    <MarqueeFrame>
      <EdgeFade $side="left" $on={edges.left} aria-hidden="true" />
      <EdgeFade $side="right" $on={edges.right} aria-hidden="true" />
      <Marquee ref={scroller}>
        {row.map(([file, alt, cap]) => (
          <Shot key={file + cap}>
            <img src={photo(file)} alt={alt} />
            <FigCap>{cap}</FigCap>
          </Shot>
        ))}
      </Marquee>
    </MarqueeFrame>
  )
}

const MarqueeFrame = styled.div`
  position: relative;
`

const EdgeFade = styled.span<{ $side: 'left' | 'right'; $on: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 1;
  width: 4%;
  pointer-events: none;
  opacity: ${({ $on }) => ($on ? 1 : 0)};
  ${({ $side }) =>
    $side === 'left'
      ? css`
          left: 0;
          background: linear-gradient(90deg, #ffffff, rgba(255, 255, 255, 0));
        `
      : css`
          right: 0;
          background: linear-gradient(270deg, #ffffff, rgba(255, 255, 255, 0));
        `}
`

const MarqueeWrap = styled.div`
  margin-top: clamp(36px, 4vw, 56px);
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const Marquee = styled.div`
  display: flex;
  gap: 14px;
  overflow-x: auto;
  padding: 0 20px 8px;
  scrollbar-width: thin;
`

const Shot = styled.figure`
  margin: 0;
  flex-shrink: 0;
  width: clamp(230px, 22vw, 320px);
  position: relative;
  aspect-ratio: 4 / 3;
  border-radius: 20px;
  overflow: hidden;
  background: #f2f4f6;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`

const FieldAction = styled.div`
  margin-top: 28px;
  display: flex;
  justify-content: center;
`

const OutlineButton = styled(Link)`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 56px;
    padding: 0 26px;
    border-radius: 14px;
    background: #ffffff;
    border: 1.4px solid #d1d6db;
    color: #191f28;
    font-size: 17px;
    font-weight: 700;
    white-space: nowrap;
  }
  &&:hover { border-color: #8b95a1; }
`

const NumberBand = styled.div`
  margin-top: clamp(56px, 6vw, 88px);
  background: #0c2049;
  color: #ffffff;
`

const NumberInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(40px, 4.5vw, 72px) clamp(20px, 3.4vw, 40px);
`

const NumberGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  overflow: hidden;
  row-gap: 32px;
`

const NumberItem = styled.div`
  flex: 1 1 220px;
  min-width: 0;
  box-sizing: border-box;
  margin-left: -1px;
  padding: 4px clamp(16px, 2vw, 28px);
  border-left: 1px solid rgba(255, 255, 255, 0.16);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`

const NumberValue = styled.p`
  margin: 0;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
  color: #ffb649;
  b { font-size: clamp(42px, 4.4vw, 62px); font-weight: 800; line-height: 1.05; letter-spacing: -0.01em; }
  span { font-size: 18px; font-weight: 700; }
`

const NumberLabel = styled.p`
  margin: 8px 0 0;
  font-size: 16px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.82);
`

const NumberNote = styled.p`
  margin: 32px 0 0;
  font-size: 14px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.72);
`

const StoryGrid = styled.div`
  margin-top: clamp(32px, 4vw, 52px);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr));
  align-items: stretch;
  gap: 16px;
`

const Story = styled.figure`
  margin: 0;
  display: flex;
  flex-direction: column;
  border-radius: 24px;
  overflow: hidden;
  background: #ffffff;
`

const StoryPhoto = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  background: #e5e8eb;
  img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block; }
`

const BeforeTag = styled.span`
  position: absolute;
  left: 12px;
  top: 12px;
  padding: 4px 12px;
  border-radius: 999px;
  background: rgba(25, 31, 40, 0.82);
  color: #ffffff;
  font-size: 13px;
  font-weight: 800;
`

const AfterTag = styled(BeforeTag)`
  left: auto;
  right: 12px;
  background: #003399;
`

const StoryBody = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: clamp(24px, 2.4vw, 32px);
  blockquote {
    margin: 16px 0 0;
    flex-grow: 1;
    font-size: 17px;
    font-weight: 600;
    line-height: 1.7;
    letter-spacing: -0.01em;
    color: #333d4b;
  }
`

const StoryKind = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 800;
  color: #3d5afe;
`

const StoryTitle = styled.p`
  margin: 6px 0 0;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.4;
  letter-spacing: -0.02em;
  color: #191f28;
`

const StoryFoot = styled.figcaption`
  margin-top: 22px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding-top: 18px;
  border-top: 1px solid #d9dde2;
  b { font-size: 17px; font-weight: 800; color: #191f28; }
`

const StoryLink = styled.a`
  && {
    margin-top: 16px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 44px;
    padding: 0 16px;
    border-radius: 10px;
    border: 1px solid #c9cfd6;
    background: #ffffff;
    color: #003399;
    font-size: 15px;
    font-weight: 800;
  }
  &&:hover { background: #eef2ff; border-color: #003399; }
`

function External() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 17 17 7M8 7h9v9" />
    </svg>
  )
}

const CenterRow = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: center;
`

const PaleButton = styled(Link)`
  && {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 52px;
    padding: 0 24px;
    border-radius: 14px;
    background: #ffffff;
    border: 1.4px solid #c9d3f2;
    color: #003399;
    font-size: 16px;
    font-weight: 800;
  }
  &&:hover { border-color: #003399; }
`

const FitGrid = styled.div`
  margin-top: clamp(32px, 4vw, 52px);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 320px), 1fr));
  align-items: stretch;
  gap: 16px;
`

const FitCard = styled.article`
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: clamp(22px, 2.4vw, 30px);
  border-radius: 24px;
  background: #ffffff;
`

const FitPill = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  background: #eef2ff;
  color: #003399;
  font-size: 14px;
  font-weight: 800;
`

const FitTitle = styled.h3`
  margin: 18px 0 0;
  font-size: clamp(22px, 2vw, 26px);
  font-weight: 800;
  line-height: 1.35;
  letter-spacing: -0.025em;
  color: #191f28;
`

const FitDesc = styled.p`
  margin: 8px 0 0;
  font-size: 16px;
  line-height: 1.65;
  color: #4e5968;
`

const FitFor = styled.p`
  margin: 22px 0 10px;
  font-size: 14px;
  font-weight: 800;
  color: #3d5afe;
`

const FitList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
  li {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    min-height: 48px;
    padding: 11px 12px;
    border-radius: 12px;
    border: 1.5px solid #e5e8eb;
    color: #333d4b;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.5;
  }
`

const FitCheck = styled.span`
  flex: none;
  width: 22px;
  height: 22px;
  margin-top: 1px;
  border-radius: 6px;
  background: #003399;
  color: #ffffff;
  display: grid;
  place-items: center;
`

const FitFact = styled.p`
  margin: 20px 0 0;
  font-size: 15px;
  font-weight: 700;
  color: #191f28;
`

const ChipRow = styled.div`
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 36px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid #c9cfd6;
  background: #ffffff;
  color: #003399;
  font-size: 14px;
  font-weight: 700;
`

const FitActions = styled.div`
  margin-top: auto;
  padding-top: 24px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 16px;
`

const FitCta = styled.a`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 52px;
    padding: 0 22px;
    border-radius: 14px;
    background: #eef2ff;
    color: #003399;
    font-size: 16px;
    font-weight: 800;
  }
`

const Calc = styled.div`
  margin-top: clamp(32px, 4vw, 52px);
  display: flex;
  flex-wrap: wrap;
  border-radius: 28px;
  overflow: hidden;
  border: 1px solid #e5e8eb;
  background: #ffffff;
`

const CalcForm = styled.div`
  flex: 1 1 480px;
  min-width: 0;
  padding: clamp(24px, 3.4vw, 48px);
`

const Question = styled.p<{ $spaced?: boolean }>`
  margin: ${(p) => (p.$spaced ? '30px' : '0')} 0 14px;
  font-size: 19px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #191f28;
`

const ChoiceRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  & + p { margin-top: 30px; }
`

const Choice = styled.button<{ $on: boolean; $off?: boolean; $wide?: boolean }>`
  min-height: 52px;
  min-width: ${(p) => (p.$wide ? 96 : 84)}px;
  padding: 0 16px;
  border-radius: 12px;
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  line-height: inherit;
  cursor: ${(p) => (p.$off ? 'not-allowed' : 'pointer')};
  background: ${(p) => (p.$off ? '#F2F4F6' : p.$on ? '#EEF2FF' : '#FFFFFF')};
  border: 2px ${(p) => (p.$off ? 'dashed' : 'solid')} ${(p) => (p.$off ? '#D1D6DB' : p.$on ? '#003399' : '#D1D6DB')};
  color: ${(p) => (p.$off ? '#6B7684' : p.$on ? '#003399' : '#191F28')};
  opacity: 1;
  transition: background-color 0.2s, border-color 0.2s, color 0.2s;

  &:disabled {
    cursor: not-allowed;
    background: #f2f4f6;
    border: 2px dashed #d1d6db;
    color: #6b7684;
    opacity: 1;
  }
`

const CalcNote = styled.p`
  margin: 16px 0 0;
  font-size: 15px;
  line-height: 1.6;
  color: #4e5968;
`

const CalcResult = styled.div`
  flex: 1 1 400px;
  min-width: 0;
  padding: clamp(24px, 3.4vw, 48px);
  background: #0c2049;
  color: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const ResultMeta = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.82);
`

const ResultPrice = styled.p`
  margin: 10px 0 0;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 10px;
  span { font-size: 20px; font-weight: 700; }
  em { font-style: normal; font-size: 24px; font-weight: 800; }
`

const PriceAmount = styled.strong`
  display: inline-block;
  font-family: Gabarito, Pretendard, sans-serif;
  font-variant-numeric: tabular-nums;
  font-size: clamp(50px, 5.6vw, 78px);
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.02em;
  color: #ffb649;
`

const ResultSub = styled.p`
  margin: 14px 0 0;
  font-size: 16px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.86);
  & + & { margin-top: 4px; }
`

const BadgeRow = styled.div`
  margin-top: 22px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  font-size: 14px;
  font-weight: 700;
`

const ActionRow = styled.div`
  margin-top: 26px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`

const WhiteButton = styled(Link)`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 56px;
    padding: 0 24px;
    border-radius: 14px;
    background: #ffffff;
    color: #0c2049;
    font-size: 17px;
    font-weight: 800;
    white-space: nowrap;
  }
  &&:hover { background: #eef2ff; }
`

const GhostButton = styled(Link)`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 56px;
    padding: 0 22px;
    border-radius: 14px;
    border: 1.4px solid rgba(255, 255, 255, 0.4);
    color: #ffffff;
    font-size: 17px;
    font-weight: 700;
    white-space: nowrap;
  }
`

const ConsultLink = styled(Link)`
  && {
    display: inline-flex;
    align-items: center;
    min-height: 56px;
    padding: 0 6px;
    color: #ffffff;
    font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.6;
    text-decoration: underline;
    text-underline-offset: 4px;
  }
`

const TermGrid = styled.div`
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr));
  gap: 16px;
`

const Term = styled.div`
  padding: clamp(22px, 2.4vw, 32px);
  border-radius: 24px;
  background: #f2f4f6;
  h3 { margin: 0; font-size: 19px; font-weight: 800; letter-spacing: -0.02em; color: #191f28; }
  ul { margin: 14px 0 0; padding: 0 0 0 20px; display: flex; flex-direction: column; gap: 8px; font-size: 16px; line-height: 1.65; color: #333d4b; list-style: disc; }
  strong { display: block; margin: 14px 0 0; font-size: 16px; font-weight: 600; color: #191f28; }
`

const PricePromise = styled.p`
  margin: 20px 0 0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 17px;
  font-weight: 700;
  color: #191f28;
`

const PromiseIcon = styled.span`
  flex: none;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #eef2ff;
  color: #003399;
  display: grid;
  place-items: center;
`

const ProgressBlock = styled.div`
  margin-top: clamp(40px, 5vw, 64px);
  position: relative;
`

const ProgressTrack = styled.div`
  height: 4px;
  border-radius: 2px;
  background: #dce3f0;
  overflow: hidden;
  i {
    display: block;
    width: 100%;
    height: 100%;
    background: #003399;
    transform-origin: 0 50%;
    transform: scaleX(0);
  }
`

const StepGrid = styled.ol`
  margin: 20px 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
  gap: 14px;
`

const StepCard = styled.li`
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-radius: 20px;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #e5e8eb;
`

const StepPhoto = styled.div`
  aspect-ratio: 16 / 15;
  overflow: hidden;
  background: #e5e8eb;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`

const ChatMock = styled.div`
  aspect-ratio: 16 / 15;
  overflow: hidden;
  background: #f2f4f6;
  padding: 10px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
`

const ChatHead = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
  i { width: 22px; height: 22px; border-radius: 8px; background: #003399; color: #ffffff; display: grid; place-items: center; font-style: normal; font-size: 11px; font-weight: 800; }
  b { font-size: 12px; font-weight: 800; color: #191f28; }
`

const chatBubble = css`
  max-width: 82%;
  padding: 6px 9px;
  font-size: 11.5px;
  line-height: 1.45;
  font-weight: 600;
`

const ChatLeft = styled.span`
  ${chatBubble};
  align-self: flex-start;
  border-radius: 4px 12px 12px 12px;
  background: #ffffff;
  color: #191f28;
`

const ChatRight = styled.span`
  ${chatBubble};
  align-self: flex-end;
  border-radius: 12px 4px 12px 12px;
  background: #003399;
  color: #ffffff;
`

const StepBody = styled.div`
  padding: 18px 18px 20px;
`

const StepNo = styled.span`
  display: inline-grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #dce3f0;
  color: #4e5968;
  border: 2px solid #dce3f0;
  box-sizing: border-box;
  font-family: Gabarito, Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 700;
  transition: background-color 0.28s ease-out, color 0.28s ease-out, border-color 0.28s ease-out;
`

const StepTitle = styled.p`
  margin: 12px 0 0;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #191f28;
`

const StepDesc = styled.p`
  margin: 4px 0 0;
  font-size: 15px;
  line-height: 1.6;
  color: #4e5968;
`

const ManagerRow = styled.div`
  margin-top: 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
`

const ManagerNote = styled.p`
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 999px;
  background: #f2f4f6;
  font-size: 16px;
  font-weight: 700;
  color: #191f28;

  svg { flex: none; display: block; }
`

const Assure = styled.div`
  margin-top: clamp(32px, 4vw, 48px);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 28px;
  padding: 18px 24px;
  border-radius: 18px;
  background: #f2f4f6;
`

const AssureLabel = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: #003399;
`

const AssureList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  font-size: 16px;
  font-weight: 600;
  color: #333d4b;
  span { display: inline-flex; align-items: center; gap: 8px; }
  svg { color: #003399; display: block; flex: none; }
`

const SplitHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
`

const AfterGrid = styled.div`
  margin-top: clamp(32px, 4vw, 52px);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr));
  align-items: stretch;
  gap: 16px;
`

const AfterCard = styled.article`
  display: flex;
  flex-direction: column;
  padding: clamp(22px, 2.4vw, 30px);
  border-radius: 24px;
  background: #ffffff;
  word-break: keep-all;
`

const AfterNo = styled.p`
  margin: 0;
  font-family: Gabarito, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1.6;
  color: #3d5afe;
`

const AfterTitle = styled.h3`
  margin: 10px 0 0;
  font-size: clamp(20px, 1.8vw, 24px);
  font-weight: 800;
  line-height: 1.35;
  letter-spacing: -0.025em;
  color: #191f28;
`

const AfterBody = styled.p`
  margin: 12px 0 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.7;
  color: #4e5968;
  text-wrap: pretty;
`

const CrewGrid = styled.div`
  margin-top: clamp(32px, 4vw, 52px);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 400px), 1fr));
  gap: 16px;
`

const CrewFeature = styled.figure`
  margin: 0;
  display: flex;
  flex-direction: column;
  border-radius: 24px;
  overflow: hidden;
  background: #ffffff;
  word-break: keep-all;
`

const CrewPhoto = styled.div`
  position: relative;
  flex: 1 0 auto;
  aspect-ratio: 16 / 9;
  background: #e5e8eb;
  img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block; }
`

const CrewCaption = styled.figcaption`
  display: flex;
  flex-direction: column;
  padding: clamp(22px, 2.6vw, 32px);
`

const CrewQuote = styled.p`
  margin: 0;
  font-size: clamp(19px, 1.6vw, 22px);
  font-weight: 700;
  line-height: 1.6;
  letter-spacing: -0.02em;
  color: #191f28;
`

const CrewNote = styled.p`
  margin: 8px 0 0;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.6;
  color: #4e5968;
`

const CrewLinkRow = styled.div`
  margin-top: auto;
  padding-top: 4px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 4px 12px;
`

const CrewMore = styled.a`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 44px;
  font-size: 15px;
  font-weight: 800;
  color: #003399;
  text-decoration: none;
`

const CrewSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const CrewMini = styled.figure`
  margin: 0;
  flex: 1 1 auto;
  display: flex;
  align-items: stretch;
  gap: clamp(14px, 1.6vw, 20px);
  padding: 14px;
  border-radius: 24px;
  background: #ffffff;
  word-break: keep-all;
`

const CrewThumb = styled.div`
  flex: none;
  width: clamp(96px, 9vw, 128px);
  aspect-ratio: 1 / 1;
  align-self: center;
  border-radius: 16px;
  overflow: hidden;
  background: #e5e8eb;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`

const CrewMiniBody = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 6px 8px 0 0;
`

const CrewMiniQuote = styled.blockquote`
  margin: 0;
  font-size: clamp(16px, 1.3vw, 18px);
  font-weight: 700;
  line-height: 1.55;
  letter-spacing: -0.02em;
  color: #191f28;
`

const CrewMiniNote = styled.p`
  margin: 6px 0 0;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.6;
  color: #4e5968;
`

const CrewWorkTitle = styled.h3`
  margin: clamp(48px, 5vw, 64px) 0 0;
  font-size: clamp(22px, 2vw, 28px);
  font-weight: 800;
  letter-spacing: -0.025em;
  color: #191f28;
`

const CrewWorkGrid = styled.div`
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
  gap: 16px;
`

const CrewWorkCard = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 24px;
  border-radius: 24px;
  background: #ffffff;
`

const CrewWorkIconBox = styled.span`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: #eef2ff;
  color: #003399;
  display: grid;
  place-items: center;
`

const CrewWorkName = styled.p`
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #191f28;
`

const CrewWorkDesc = styled.p`
  margin: 4px 0 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.6;
  color: #4e5968;
`

const CrewBand = styled.div`
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: clamp(28px, 4vw, 64px);
  padding: clamp(28px, 4vw, 56px);
  border-radius: 28px;
  background: #0c2049;
  color: #ffffff;
  box-sizing: border-box;
`

const CrewBandCopy = styled.div`
  flex: 1 1 520px;
  min-width: 0;
`

const CrewBandKicker = styled.p`
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.6;
  color: #ffd08a;
`

const CrewBandTitle = styled.h3`
  margin: 0;
  font-size: clamp(24px, 2.3vw, 34px);
  font-weight: 800;
  line-height: 1.35;
  letter-spacing: -0.025em;
  color: #ffffff;
`

const CrewCheckBlock = styled.div`
  margin-top: 26px;
`

const CrewCheckLabel = styled.p`
  margin: 0 0 6px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.7);
`

const CrewCheckList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
  column-gap: 40px;
  li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 7px 0;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.92);
  }
`

const CrewCheckBox = styled.span`
  flex-shrink: 0;
  margin-top: 2px;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  background: #ffb649;
  color: #0c2049;
  display: grid;
  place-items: center;
`

const CrewKitRow = styled.div`
  margin-top: 24px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`

const CrewKitLabel = styled.span`
  margin-right: 4px;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.78);
`

const CrewKit = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 15px;
  font-weight: 700;
  line-height: 1.6;
  color: #ffffff;
`

const crewBob = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0); }
`

const CrewPhone = styled.figure`
  margin: 0 auto;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  figcaption { font-size: 14px; line-height: 1.6; color: rgba(255, 255, 255, 0.72); }
`

const CrewPhoneFrame = styled.div`
  width: 250px;
  height: 520px;
  border-radius: 38px;
  padding: 9px;
  background: #050c1c;
  border: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow: 0 30px 60px -28px rgba(0, 0, 0, 0.7);
  box-sizing: border-box;
  will-change: transform;
  animation: ${crewBob} 5200ms ease-in-out infinite;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`

const CrewPhoneScreen = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 30px;
  overflow: hidden;
  background: #ffffff;
  img { width: 100%; height: 100%; object-fit: cover; object-position: top; display: block; }
`

const CrewApplyRow = styled.div`
  margin-top: 24px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 28px;
`

const CrewApply = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 44px;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.6;
  color: #003399;
  text-decoration: none;
`

const AreaInner = styled(Inner)`
  display: flex;
  flex-wrap: wrap;
  gap: clamp(48px, 6vw, 96px);
  > div { flex: 1 1 380px; min-width: 0; }
  > div:last-child { flex: 1.4 1 480px; }
`

const RegionList = styled.ul`
  margin: 24px 0 0;
  padding: 0;
  list-style: none;
  border-top: 2px solid #191f28;
  li { display: flex; gap: 16px; padding: 18px 0; border-bottom: 1px solid #e5e8eb; font-size: 16px; }
  b { flex: 0 0 76px; font-weight: 800; color: #191f28; }
  span { color: #4e5968; }
`

const WeekendNote = styled.p`
  margin: 16px 0 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #4e5968;
`

const RegionButton = styled(Link)`
  && {
    margin-top: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 52px;
    padding: 0 22px;
    border-radius: 14px;
    background: #ffffff;
    border: 1.4px solid #d1d6db;
    color: #191f28;
    font-size: 16px;
    font-weight: 700;
  }
`

const FaqList = styled.div`
  margin-top: 28px;
  border-top: 2px solid #191f28;
`

const FaqItem = styled.div`
  border-bottom: 1px solid #e5e8eb;
`

const FaqButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  min-height: 72px;
  padding: 18px 4px;
  background: transparent;
  text-align: left;
  font-size: clamp(17px, 1.4vw, 19px);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.5;
  color: #191f28;
  cursor: pointer;
  > span { display: flex; gap: 12px; align-items: baseline; min-width: 0; }
`

const Q = styled.span`
  flex-shrink: 0;
  font-size: 18px;
  font-weight: 800;
  color: #003399;
`

const Caret = styled.svg<{ $open: boolean }>`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  fill: none;
  stroke: #4e5968;
  stroke-width: 2.2;
  stroke-linecap: round;
  stroke-linejoin: round;
  transform: rotate(${(p) => (p.$open ? '180deg' : '0deg')});
  transition: transform 0.2s;
`

const FaqAnswer = styled.div`
  padding: 0 4px 24px 31px;
  font-size: 16px;
  line-height: 1.75;
  color: #4e5968;
  p { margin: 0 0 8px; }
  &[hidden] { display: none; }
`

const NewsAll = styled(Link)`
  && {
    display: inline-flex;
    align-items: center;
    min-height: 52px;
    padding: 0 22px;
    border-radius: 14px;
    border: 1.5px solid #d1d6db;
    background: #ffffff;
    color: #191f28;
    font-size: 16px;
    font-weight: 700;
  }
  &&:hover { background: #e5e8eb; }
`

const NewsGrid = styled.div`
  margin-top: clamp(28px, 3vw, 40px);
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 320px), 1fr));
  gap: clamp(28px, 2.6vw, 40px) clamp(20px, 2vw, 24px);
`

const NewsCard = styled.a`
  && {
    display: flex;
    flex-direction: column;
    gap: 14px;
    color: #191f28;
    font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  }
  &&:hover { color: #003399; }
`

const NewsPhoto = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: 16px;
  overflow: hidden;
  background: #e5e8eb;
  border: 1px solid #d9dde2;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`

const NewsTitle = styled.p`
  && {
    margin: 0;
    font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
    font-size: clamp(18px, 1.5vw, 20px);
    font-weight: 700;
    line-height: 1.45;
    letter-spacing: -0.02em;
    color: inherit;
    text-wrap: pretty;
  }
`

const NewsMeta = styled.span`
  && {
    margin-top: -6px;
    font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.6;
    color: #4e5968;
  }
`

const ApplyInner = styled(Inner)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: clamp(36px, 5vw, 80px);
  > div { flex: 1 1 420px; min-width: 0; }
`

const ContactGrid = styled.div`
  margin-top: 32px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
  gap: 12px;
  max-width: 520px;
`

const ContactCard = styled.a`
  && {
    display: flex;
    align-items: center;
    gap: 14px;
    min-height: 76px;
    padding: 14px 18px;
    border-radius: 18px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #ffffff;
  }
`

const ApplyAside = styled.p`
  margin: 20px 0 0;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.86);
  a {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    color: #ffb649;
    font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
    font-size: 16px;
    font-weight: 800;
    line-height: 1.6;
  }
`

const ApplyForm = styled.form`
  flex: 1 1 440px;
  min-width: 0;
  background: #ffffff;
  color: #191f28;
  border-radius: 28px;
  padding: clamp(24px, 3vw, 40px);
  box-shadow: 0 40px 80px -44px rgba(0, 0, 0, 0.55);
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 17px;
  line-height: 1.6;
  word-break: keep-all;
  box-sizing: border-box;
`

const FormLead = styled.p`
  margin: 0;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.6;
  color: #4e5968;
`

const FormTitle = styled.p`
  margin: 4px 0 0;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.4;
  letter-spacing: -0.02em;
`

const Label = styled.label`
  display: block;
  margin-top: 16px;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.6;
  &:first-of-type { margin-top: 24px; }
`

const Req = styled.span`
  color: #c62828;
`

const Field = styled.input<{ $bad: boolean }>`
  margin-top: 8px;
  width: 100%;
  height: 52px;
  padding: 0 16px;
  border-radius: 12px;
  border: 1.5px solid ${(p) => (p.$bad ? '#C62828' : '#D1D6DB')};
  background: #ffffff;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 17px;
  font-weight: 400;
  line-height: 1.6;
  color: #191f28;
  box-sizing: border-box;
`

const Select = styled.select<{ $empty: boolean }>`
  margin-top: 8px;
  width: 100%;
  height: 52px;
  padding: 0 16px;
  border-radius: 12px;
  border: 1.5px solid #d1d6db;
  background: #ffffff;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 17px;
  font-weight: 400;
  line-height: 1.6;
  color: ${(p) => (p.$empty ? '#6B7684' : '#191F28')};
  box-sizing: border-box;
  cursor: pointer;
`

const Alert = styled.p`
  margin: 8px 0 0;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.6;
  color: #c62828;
`

const Agree = styled.label`
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.6;
  cursor: pointer;
  input { width: 22px; height: 22px; margin: 0; accent-color: #003399; }
`

const Privacy = styled.div`
  margin-top: 8px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f2f4f6;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.65;
  color: #4e5968;
  p { margin: 0; }
  p + p { margin-top: 8px; }
`

const Fail = styled.p`
  margin: 16px 0 0;
  padding: 14px 16px;
  border-radius: 12px;
  background: #fdecec;
  color: #9b1c1c;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.6;
`

const Submit = styled.button`
  margin-top: 20px;
  width: 100%;
  min-height: 58px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 0;
  border-radius: 14px;
  background: #003399;
  color: #ffffff;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.6;
  cursor: pointer;
  &:disabled { background: #4d6fb8; color: #ffffff; }
`

const Done = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
`

const DoneIcon = styled.span`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #eef2ff;
  color: #003399;
  display: grid;
  place-items: center;
`

const DoneTitle = styled.p`
  margin: 6px 0 0;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.6;
  letter-spacing: -0.02em;
`

const DoneBody = styled.p`
  margin: 0;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.7;
  color: #4e5968;
`

const Reset = styled.button`
  margin-top: 8px;
  min-height: 48px;
  padding: 0 18px;
  border-radius: 12px;
  border: 1.4px solid #d1d6db;
  background: #ffffff;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.6;
  color: #191f28;
  cursor: pointer;
`
